import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useJsApiLoader, Libraries } from "@react-google-maps/api";
import DashboardLayout from "../../components/customer/DashboardLayout";
import useForm from "../../hooks/useForm";
import usePackageValidation, { validateWeightValue, validateDimensionValue } from "../../hooks/usePackageValidation";
import {
  customerAddressService,
  Address,
} from "../../services/customer.address.service";
import {
  utilsService,
  CurrencyObject,
  DniTypeObject,
} from "../../services/utils.service";
import {
  rateService,
  RatePayload,
  RateOption,
  CalculateExternalRatePayload,
  ExternalRateProviderResponse,
  ExternalRateServiceOption,
} from "../../services/rate.service";
import { orderService, CreateOrderRequest } from "../../services/order.service";
import { tokenService } from "../../services/token.service";
import { useAuth } from "../../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSpinner,
  faMapMarkerAlt,
  faCalculator,
  faTruckFast,
  faCheckCircle,
  faBox,
  faCreditCard,
  faTruck,
  faShieldAlt,
  faChevronLeft,
  faPencilAlt,
  faChevronRight,
  faExclamationTriangle,
  faCheckSquare,
  faSquare,
  faCalendarAlt,
  faHandPointer,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import parcelIMG from "../../assets/images/logo-elpaket-short.png";
import Swal from "sweetalert2";
import { loadStripe } from "@stripe/stripe-js";
import { stripePromise } from "../../config/stripe.config";
import { Elements } from "@stripe/react-stripe-js";
import StripePaymentForm from "../../components/common/StripePaymentForm";
import { paymentService } from "../../services/payment.service";
import { errorService } from "../../services/error.service";
import { countryService, Country } from "../../services/country.service";
import { fileCompressionService } from "../../services/fileCompression.service";
import shippingImg from "../../assets/app/shipping.png";
import cajaTransparente from "../../assets/images/CAJA_TRANSPARENTE.png";
import dropoffStoreImg from "../../assets/images/dropoff_store.jpg";

import styles from "../../styles/app/SendPackagePageV3.module.css";
import additionalStyles from "../../styles/common/AdditionalExternalServiceCard.module.css";

import AddressSelectionCard from "../../components/common/AddressSelectionCard";
import AddressCreationModal from "../../components/common/AddressCreationModal";
import EditPackageDataModal from "../../components/common/EditPackageDataModal";
import EditAddressDataModal from "../../components/common/EditAddressDataModal";
import EditAddressModal from "../../components/common/EditAddressModal";
import ServiceCard from "../../components/common/ServiceCard";
import PackageDetailsModal from "../../components/common/PackageDetailsModal";
import ServiceDetailModalV2 from "../../components/common/ServiceDetailModalV2";
import OrderSuccessCard from "../../components/common/OrderSuccessCard";
import ExternalServiceCard from "../../components/common/ExternalServiceCard";
import AdditionalExternalServiceCard from "../../components/common/AdditionalExternalServiceCard";
import BoxIllustration from "../../components/common/BoxIllustration";

const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const day = String(tomorrow.getDate()).padStart(2, "0");
  const month = String(tomorrow.getMonth() + 1).padStart(2, "0");
  const year = tomorrow.getFullYear();
  return `${year}-${month}-${day}`;
};

const getMaxDate = () => {
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 7);
  const day = String(maxDate.getDate()).padStart(2, "0");
  const month = String(maxDate.getMonth() + 1).padStart(2, "0");
  const year = maxDate.getFullYear();
  return `${year}-${month}-${day}`;
};

const LIBRARIES: Libraries = ["places"];

const SendPackagePageV3: React.FC = () => {
  const [countryNamesMap, setCountryNamesMap] = useState<{ [key: string]: string }>({});

  const getCountryName = (code: string) => {
    if (!code) return "";
    const uppercaseCode = code.toUpperCase();
    return countryNamesMap[uppercaseCode] || code;
  };

  const navigate = useNavigate();
  const { logout } = useAuth();
  const [searchParams] = useSearchParams();
  const orderNumberParam = searchParams.get("orderNumber");


  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [currencies, setCurrencies] = useState<CurrencyObject[]>([]);
  const [rateResults, setRateResults] = useState<RateOption[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedExternalIndicesByCarrier, setSelectedExternalIndicesByCarrier] =
    useState<Map<string, Set<number>>>(new Map());

  const [externalServices, setExternalServices] = useState<ExternalRateProviderResponse[]>([]);
  const [selectedExternalOption, setSelectedExternalOption] = useState<ExternalRateServiceOption | null>(null);
  const [pickupSelected, setPickupSelected] = useState<boolean>(false);
  const [elpaketDispatcherSelected, setElpaketDispatcherSelected] = useState<boolean>(false);
  const userWantsPickupRef = React.useRef<boolean>(false);
  const userWantsElpaketDispatcherRef = React.useRef<boolean>(false);
  const [loadingExternal, setLoadingExternal] = useState<boolean>(false);

  const [isValidatingOriginZip, setIsValidatingOriginZip] = useState(false);
  const [isValidatingDestZip, setIsValidatingDestZip] = useState(false);
  const [originZipValidated, setOriginZipValidated] = useState(false);
  const [destZipValidated, setDestZipValidated] = useState(false);
  const [originZipError, setOriginZipError] = useState<string | null>(null);
  const [destZipError, setDestZipError] = useState<string | null>(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressModalType, setAddressModalType] = useState<"origin" | "destination">("origin");
  const [showEditAddressModal, setShowEditAddressModal] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState<Address | null>(null);

  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [disclaimerError, setDisclaimerError] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{ file: File; base64string: string; ext: string } | null>(null);
  const [docErrors, setDocErrors] = useState<string>("");
  const [isCompressingDoc, setIsCompressingDoc] = useState<boolean>(false);

  const handleDocumentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file = e.target.files[0];
    if (!file) return;

    const allowedExtensions = ["png", "jpg", "jpeg", "pdf"];
    const fileExt = file.name.split('.').pop()?.toLowerCase() || "";
    if (!allowedExtensions.includes(fileExt)) {
      setDocErrors(`El archivo "${file.name}" tiene una extensión no permitida. Solo se aceptan png, jpg, jpeg, pdf.`);
      return;
    }

    const maxSizeBytes = fileExt === "pdf" ? 500 * 1024 : 5 * 1024 * 1024;
    const maxWeightLabel = fileExt === "pdf" ? "500KB" : "5MB";

    if (file.size > maxSizeBytes) {
      setDocErrors(`El archivo "${file.name}" supera el peso máximo de ${maxWeightLabel}.`);
      return;
    }

    try {
      setIsCompressingDoc(true);
      setDocErrors("");

      const result = await fileCompressionService.compressFile(file, {
        maxWidth: 1920,
        maxHeight: 1920,
        quality: 0.8,
      });

      setSelectedDocument({
        file: result.file,
        base64string: result.base64string,
        ext: result.ext,
      });
    } catch (err) {
      console.error("Error al procesar/comprimir el archivo:", err);
      setDocErrors("Error al procesar el archivo.");
    } finally {
      setIsCompressingDoc(false);
      e.target.value = "";
    }
  };

  const removeDocument = () => {
    setSelectedDocument(null);
    setDocErrors("");
  };
  const [dniTypes, setDniTypes] = useState<DniTypeObject[]>([]);
  const [allReceiverCountries, setAllReceiverCountries] = useState<Country[]>([]);
  const [mpReceiverCountries, setMpReceiverCountries] = useState<Country[]>([]);

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedOriginId, setSelectedOriginId] = useState<number | null>(null);
  const [selectedDestId, setSelectedDestId] = useState<number | null>(null);
  const [paymentMethod] = useState<"card" | "paypal" | "transfer">("card");
  const [orderSuccessData, setOrderSuccessData] = useState<any | null>(null);
  const [showEditPackageModal, setShowEditPackageModal] = useState(false);
  const [showPackageDetailsModal, setShowPackageDetailsModal] = useState(false);
  const [showEditOriginModal, setShowEditOriginModal] = useState(false);
  const [showEditDestModal, setShowEditDestModal] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [showServiceDetailModal, setShowServiceDetailModal] = useState(false);
  const [selectedRateForModal, setSelectedRateForModal] = useState<RateOption | null>(null);
  const [allowRedirect, setAllowRedirect] = useState(false);
  const [stripeError, setStripeError] = useState(false);
  const lastCalculatedPayloadRef = React.useRef<string>("");
  const [packages, setPackages] = useState<Array<{
    length: string;
    width: string;
    height: string;
    weight: string;
    fob: string;
    packageContent: string;
    isFragile?: boolean;
    errors?: {
      length?: string | null;
      width?: string | null;
      height?: string | null;
      weight?: string | null;
      fob?: string | null;
      packageContent?: string | null;
    };
  }>>([{ length: "", width: "", height: "", weight: "", fob: "", packageContent: "", isFragile: false }]);
  const receiverCountries = packages.length > 1 ? mpReceiverCountries : allReceiverCountries;

  const handleAddPackage = () => {
    if (packages.length >= 10) {
      Swal.fire({
        icon: "warning",
        title: "Límite alcanzado",
        text: "El número máximo de paquetes (MP) permitido es 10.",
        confirmButtonColor: "#3085d6",
        returnFocus: false
      });
      return;
    }
    setPackages(prev => [...prev, { length: "", width: "", height: "", weight: "", fob: "", packageContent: "", isFragile: false }]);
  };

  const handleRemovePackage = (index: number) => {
    setPackages(prev => prev.filter((_, i) => i !== index));
  };

  const handlePackageChange = (index: number, field: string, value: any) => {
    let finalValue = value;
    if (['length', 'width', 'height', 'weight', 'fob'].includes(field) && typeof value === 'string') {
      finalValue = value.replace(/-/g, '');
    }
    setPackages(prev => prev.map((pkg, i) => {
      if (i === index) {
        return { ...pkg, [field]: finalValue, errors: { ...pkg.errors, [field]: null } };
      }
      return pkg;
    }));
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: LIBRARIES,
  });

  const customerDataFromToken = tokenService.getCustomerDataToken()?.customerData;

  const initialValues = {
    origin: "US",
    destination: "",
    originZip: "",
    destinationZip: "",
    weight: "" as number | "",
    width: "" as number | "",
    height: "" as number | "",
    length: "" as number | "",
    fob: "" as number | "",
    unitSystem: "imperial" as "metric" | "imperial",
    currency: "USD",
    parcelOrdDoc: "PARCEL",
    serviceType: "A",
    selectedService: "",
    idCarrier: 0,
    idCarrierService: undefined as number | undefined,
    pickupDate: getTomorrowDate(),
    hasBattery: false,
    originNickname: "",
    originAddressLine1: "",
    originAddressLine2: "",
    originCity: "",
    originState: "",
    originFirstName: customerDataFromToken?.FirstName || "",
    originLastName: customerDataFromToken?.LastName || "",
    originPhone: customerDataFromToken?.PhoneNumber || "",
    originEmail: customerDataFromToken?.Email || "",
    originKindOfDNI: customerDataFromToken?.KindOfDNI || "",
    originDNI: customerDataFromToken?.DNI || "",
    destinationNickname: "",
    destinationAddressLine1: "",
    destinationAddressLine2: "",
    destinationCity: "",
    destinationState: "",
    packageContent: "",
    receiverFirstName: "",
    receiverLastName: "",
    receiverEmail: "",
    receiverPhone: "",
    receiverKindOfDNI: "",
    receiverDNI: "",
    municipalityDestiny: "",
    stateDestiny: "",
  };

  const validationRules = {
    weight: (value: number | "", allValues: any) =>
      validateWeightValue(value, allValues?.unitSystem),
    width: (value: number | "", allValues: any) =>
      validateDimensionValue(value, "Ancho", allValues?.unitSystem),
    height: (value: number | "", allValues: any) =>
      validateDimensionValue(value, "Alto", allValues?.unitSystem),
    length: (value: number | "", allValues: any) =>
      validateDimensionValue(value, "Largo", allValues?.unitSystem),
    fob: (value: number | "") =>
      !value || value <= 0 ? "El valor FOB debe ser mayor a 0" : null,
    packageContent: (value: string) => {
      if (!value) return "La descripción del contenido es requerida";
      if (value.length > 255) return "La descripción no puede exceder los 255 caracteres";
      return null;
    },
    selectedService: (value: string) =>
      !value ? "Debes seleccionar un servicio" : null,
    receiverFirstName: (value: string) => !value ? "El nombre es requerido" : null,
    receiverLastName: (value: string) => !value ? "El apellido es requerido" : null,
    receiverEmail: (value: string) => {
      if (!value) return "El email es requerido";
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return "Email inválido";
      return null;
    },
    receiverPhone: (value: string) => !value ? "El teléfono es requerido" : null,
    receiverKindOfDNI: (value: string) => !value ? "El tipo de documento es requerido" : null,
    receiverDNI: (value: string, allValues: any) => {
      if (!value) return "El documento es requerido";
      const selectedDniObj = dniTypes.find((t) => t.kindOfDNI === allValues.receiverKindOfDNI);
      if (selectedDniObj && selectedDniObj.mask) {
        const regex = new RegExp(selectedDniObj.mask);
        if (!regex.test(value)) {
          return `El documento no coincide con el formato requerido.`;
        }
      }
      return null;
    },
  };

  const { values, errors, handleChange, setValues, setErrors, validate } =
    useForm(initialValues, validationRules);

  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      if (orderSuccessData || isProcessingOrder || allowRedirect) return;
      if (!(currentStep > 1 || values.originFirstName !== "" || values.receiverFirstName !== "")) return;

      let target = e.target as HTMLElement | null;
      while (target && target.tagName !== "A") {
        target = target.parentElement;
      }

      if (target) {
        const href = target.getAttribute("href");
        const isLogout = target.textContent?.includes("Salir") || target.innerText?.includes("Salir");

        if (isLogout || (href && (href.startsWith("/") || href.startsWith(window.location.origin)))) {
          e.preventDefault();
          e.stopPropagation();

          Swal.fire({
            title: "¿Estás seguro de que deseas salir?",
            text: "Si sales ahora perderás toda la información ingresada para este envío.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ffc114",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Sí, salir",
            cancelButtonText: "Cancelar"
          }).then(async (result) => {
            if (result.isConfirmed) {
              if (isLogout) {
                await logout();
              } else if (href) {
                navigate(href);
              }
            }
          });
        }
      }
    };

    document.addEventListener("click", handleAnchorClick, true);
    return () => {
      document.removeEventListener("click", handleAnchorClick, true);
    };
  }, [orderSuccessData, isProcessingOrder, allowRedirect, currentStep, values.originFirstName, values.receiverFirstName, navigate, logout]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!orderSuccessData && !isProcessingOrder && !allowRedirect && (currentStep > 1 || values.originFirstName !== "" || values.receiverFirstName !== "")) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [orderSuccessData, isProcessingOrder, allowRedirect, currentStep, values.originFirstName, values.receiverFirstName]);

  // --- Effects ---
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const resAll = await countryService.getCountryLists();
        const resMp = await countryService.getCountryListsMp();
        setAllReceiverCountries(resAll.receivers);
        setMpReceiverCountries(resMp.receivers);
        const map: { [key: string]: string } = {};
        [...resAll.senders, ...resAll.receivers].forEach((c) => {
          if (c.iso2Country) {
            map[c.iso2Country.toUpperCase()] = c.name;
          }
        });
        setCountryNamesMap(map);
      } catch (error) {
        console.error("Error fetching country lists:", error);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    stripePromise.then((stripe) => {
      if (!stripe) setStripeError(true);
    });
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoadingAddresses(true);
        const [addressData, currencyData] = await Promise.all([
          customerAddressService.getAddresses(),
          utilsService.getCurrencies(),
        ]);
        const allAddrs = addressData.all || [];
        setAddresses(allAddrs);
        setCurrencies(currencyData);
        if (currencyData.length > 0) {
          const usd = currencyData.find((c) => c.isO3Currency === "USD");
          if (usd) setValues((prev: any) => ({ ...prev, currency: "USD" }));
        }

        const origins = allAddrs.filter((a) => (a.isO2Country || "").toUpperCase() === "US" && a.type === "O");
        const defaultOrigin = origins.find((a) => a.isDefault) || origins[0];
        if (defaultOrigin) {
          setSelectedOriginId(defaultOrigin.id || null);
          setValues((prev: any) => ({
            ...prev,
            originZip: defaultOrigin.zipCode,
            originNickname: defaultOrigin.nickname,
            originAddressLine1: defaultOrigin.addressLine1,
            originAddressLine2: defaultOrigin.addressLine2 || "",
            originCity: defaultOrigin.city,
            originState: defaultOrigin.state,
            originFirstName: defaultOrigin.addressSubjet?.firstName || prev.originFirstName,
            originLastName: defaultOrigin.addressSubjet?.lastName || prev.originLastName,
            originPhone: defaultOrigin.addressSubjet?.phoneNumber || prev.originPhone,
            originEmail: defaultOrigin.addressSubjet?.email || prev.originEmail,
            originKindOfDNI: defaultOrigin.addressSubjet?.kindOfDNI || prev.originKindOfDNI,
            originDNI: defaultOrigin.addressSubjet?.dni || prev.originDNI,
          }));
          validateZip("origin", defaultOrigin.zipCode, "US");
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setLoadingAddresses(false);
      }
    };
    fetchInitialData();
  }, [setValues]);

  useEffect(() => {
    if (values.parcelOrdDoc === "DOC" && values.hasBattery) {
      setValues((prev: any) => ({ ...prev, hasBattery: false }));
    }
  }, [values.parcelOrdDoc, values.hasBattery, setValues]);

  useEffect(() => {
    const fetchDniData = async () => {
      if (values.destination) {
        try {
          const dniData = await utilsService.getDniTypes(values.destination);
          setDniTypes(dniData);
        } catch (error) {
          console.error("Error fetching DNI types:", error);
        }
      }
    };
    fetchDniData();
  }, [values.destination]);

  useEffect(() => {
    if (!values.destination) {
      setErrors((prev: any) => ({ ...prev, destination: undefined }));
      return;
    }
    const country = receiverCountries.find((c) => c.iso2Country === values.destination);
    if (!country) return;
    if (values.serviceType === "A" && !country.air) {
      setErrors((prev: any) => ({ ...prev, destination: "Servicio Aéreo no disponible para este destino" }));
    } else if (values.serviceType === "M" && !country.maritime) {
      setErrors((prev: any) => ({ ...prev, destination: "Servicio Marítimo no disponible para este destino" }));
    } else {
      setErrors((prev: any) => ({ ...prev, destination: undefined }));
    }
  }, [values.destination, values.serviceType, receiverCountries, setErrors]);

  useEffect(() => {
    const checkOrderResume = async () => {
      if (orderNumberParam) {
        setIsProcessingOrder(true);
        try {
          const resume = await orderService.getOrderResume(orderNumberParam);
          if (resume.statusCode === 200) {
            setOrderSuccessData({
              isFromResume: true,
              id: resume.data.id,
              trackingNumber: resume.data.trackingNumber,
              carrier: {
                carrierName: resume.data.carrierName,
                carrierBase64: resume.base64,
              },
              base64Pdf: resume.base64Pdf,
              external: { base64: resume.base64externalReference },
              externalService: resume.data.externalService || (resume.base64externalReference ? [{ serviceBase64: resume.base64externalReference }] : undefined),
              additionalService: (resume.data as any).additionalService,
              receiverFirstName: resume.data.reciberName.split(" ")[0],
              receiverLastName: resume.data.reciberName.split(" ").slice(1).join(" "),
              destinationCity: resume.data.reciberDestiny.split(", ")[0],
              destinationState: resume.data.reciberDestiny.split(", ")[1],
              weight: resume.data.weight,
              packageContent: resume.data.content,
              hasSecondaryLabel: resume.data.hasAdditionalService,
            });
            setShowResults(false);
          } else {
            errorService.showErrorAlert(resume, "Error");
            navigate(-1);
          }
        } catch (error) {
          errorService.showErrorAlert(error, "Error");
          navigate(-1);
        } finally {
          setIsProcessingOrder(false);
        }
      }
    };
    checkOrderResume();
  }, [orderNumberParam, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  useEffect(() => {
    if (rateResults.length > 0) {
      setSelectedExternalIndicesByCarrier((prev) => {
        const newMap = new Map<string, Set<number>>();
        rateResults.forEach((rate) => {
          const key = getServiceKey(rate);
          if (prev.has(key)) {
            newMap.set(key, prev.get(key)!);
          } else {
            newMap.set(key, rateService.getInitialExternalIndices(rate));
          }
        });
        return newMap;
      });
    }
  }, [rateResults]);

  // Fetch external rates when carrier selection or shipment details change
  const fetchExternalRatesHelper = async (sourceValues?: any) => {
    const src = sourceValues || values;
    if (src.origin === src.destination) {
      if (rateResults[0]?.externalServices && rateResults[0].externalServices.length > 0) {
        setExternalServices(rateResults[0].externalServices as any);
      }
      setLoadingExternal(false);
      return;
    }
    if (!src.selectedService || !src.idCarrier) {
      setExternalServices([]);
      setSelectedExternalOption(null);
      setPickupSelected(false);
      setElpaketDispatcherSelected(false);
      return;
    }
    try {
      setLoadingExternal(true);

      const mappedPackages = sourceValues.parcelOrdDoc === "DOC"
        ? [{ height: 0, width: 0, length: 0, weight: 1 }]
        : packages.map(pkg => ({
            height: Number(pkg.height) || 0,
            width: Number(pkg.width) || 0,
            length: Number(pkg.length) || 0,
            weight: Number(pkg.weight) || 0,
          }));

      const payload: CalculateExternalRatePayload = {
        idCarrier: sourceValues.idCarrier,
        iso2Origin: sourceValues.origin,
        height: mappedPackages[0]?.height || 0,
        width: mappedPackages[0]?.width || 0,
        length: mappedPackages[0]?.length || 0,
        weight: mappedPackages.reduce((acc, p) => acc + p.weight, 0),
        unitSystem: sourceValues.unitSystem === "metric" ? "METRIC" : "IMPERIAL",
        zipcodeOrigin: sourceValues.originZip,
        addressLineOrigin: sourceValues.originAddressLine1,
        cityOrigin: sourceValues.originCity,
        stateOrigin: sourceValues.originState,
        pickupDate: sourceValues.pickupDate,
        packages: mappedPackages,
      };
      const results = await rateService.calculateExternalRate(payload);
      setExternalServices(results);
      
      let matchedOption: ExternalRateServiceOption | null = null;
      if (selectedExternalOption) {
        const allNewOptions = results.flatMap(r => r.options);
        matchedOption = allNewOptions.find(o => o.serviceName === selectedExternalOption.serviceName) || null;
      }

      if (matchedOption) {
        setSelectedExternalOption(matchedOption);
      } else if (results.length > 0 && results[0].options.length > 0) {
        setSelectedExternalOption(results[0].options[0]);
      } else {
        setSelectedExternalOption(null);
      }

      if (results.length > 0) {
        const hasPickup = !!results[0].pickUpService;
        const hasDispatcher = !!results[0].elpaketDispatcherService;

        if (userWantsElpaketDispatcherRef.current && hasDispatcher) {
          setElpaketDispatcherSelected(true);
          setPickupSelected(false);
        } else if (userWantsPickupRef.current && hasPickup) {
          setPickupSelected(true);
          setElpaketDispatcherSelected(false);
        } else {
          setPickupSelected(false);
          setElpaketDispatcherSelected(false);
        }
      }
    } catch (error) {
      console.error("Error fetching external rates:", error);
      setExternalServices([]);
      setSelectedExternalOption(null);
      setPickupSelected(false);
      setElpaketDispatcherSelected(false);
    } finally {
      setLoadingExternal(false);
    }
  };

  // --- Computed ---
  const getServiceKey = (rate: RateOption) => `${rate.carrierName}-${rate.idCarrierService || 0}`;

  const currentSelectedRate = React.useMemo(() => {
    if (!rateResults.length) return null;
    if (values.origin === values.destination) {
      return rateResults.find((r) => r.carrierName === values.selectedService) || rateResults[0];
    }
    return rateResults.find(
      (r) => r.carrierName === values.selectedService && r.idCarrierService === values.idCarrierService,
    ) || rateResults[0];
  }, [values.selectedService, values.idCarrierService, values.origin, values.destination, rateResults]);

  const selectExternalService = (rate: RateOption, newIndices: Set<number>) => {
    const key = getServiceKey(rate);
    setSelectedExternalIndicesByCarrier((prev) => {
      const newMap = new Map(prev);
      newMap.set(key, newIndices);
      return newMap;
    });
  };

  const dynamicTotal = React.useMemo(() => {
    const isNational = values.origin === values.destination;

    if (isNational) {
      const nationalRate = rateResults[0];
      if (!nationalRate) { console.log('[TOTAL] isNational=true but no nationalRate'); return 0; }

      // Carrier service price: find the selected UPS sub-service
      const services = nationalRate.services || [];
      const selectedServiceEntry = services.find(s => s.idCarrierService === values.idCarrierService);
      const carrierServicePrice = selectedServiceEntry
        ? selectedServiceEntry.serviceSubTotal
        : (services[0]?.serviceSubTotal ?? 0);

      // Additional: elPaket dispatcher OR pickup
      const elpaketSvc = (externalServices[0] as any)?.elpaketDispatcherService;
      const pickupSvc = (externalServices[0] as any)?.pickUpService;
      const elpaketPrice = (elpaketDispatcherSelected && elpaketSvc) ? (elpaketSvc.serviceSubTotal ?? 0) : 0;
      const pickupPrice = (pickupSelected && !elpaketDispatcherSelected && pickupSvc) ? (pickupSvc.serviceSubTotal ?? 0) : 0;

      return carrierServicePrice + elpaketPrice + pickupPrice;
    }


    // ── International mode ──
    if (!currentSelectedRate) return 0;
    const carrierBaseRate = currentSelectedRate.rate || 0;
    const externalPrice = (selectedExternalOption && !elpaketDispatcherSelected) ? selectedExternalOption.serviceSubTotal : 0;
    const elpaketPrice = (elpaketDispatcherSelected && externalServices[0]?.elpaketDispatcherService) ? externalServices[0].elpaketDispatcherService.serviceSubTotal : 0;
    const pickupPrice = (pickupSelected && !elpaketDispatcherSelected && externalServices[0]?.pickUpService) ? externalServices[0].pickUpService.serviceSubTotal : 0;
    return carrierBaseRate + externalPrice + pickupPrice + elpaketPrice;
  }, [currentSelectedRate, rateResults, selectedExternalOption, pickupSelected, elpaketDispatcherSelected, externalServices, values.origin, values.destination, values.idCarrierService]);



  const getEstimatedArrivalText = (rate: RateOption) => {
    const isThisSelected = values.selectedService === rate.carrierName && values.idCarrierService === rate.idCarrierService;
    const activeExternalOption = isThisSelected ? selectedExternalOption : null;

    if (!activeExternalOption) {
      if (rate.estimatedMinDays && rate.estimatedMaxDays) {
        return `Entrega estimada: ${rate.estimatedMinDays} - ${rate.estimatedMaxDays} días`;
      }
      return "";
    }

    if (!activeExternalOption.transitDays || activeExternalOption.transitDays === "N/A") {
      return "El tiempo de entrega final puede ajustarse a la jornada de los couriers.";
    }

    const extDays = parseInt(activeExternalOption.transitDays) || 0;
    const minDays = rate.estimatedMinDays || 0;
    const maxDays = rate.estimatedMaxDays || 0;

    // Si se seleccionó pickup, se usa values.pickupDate como inicio y se suma 1 día de recogida.
    // De lo contrario (Drop off), se empieza desde hoy y no se suma día de recogida.
    const start = pickupSelected ? values.pickupDate : new Date().toISOString().split('T')[0];
    const pickupExtraDay = pickupSelected ? 1 : 0;

    const addBusinessDays = (startDateStr: string, daysToAdd: number): Date => {
      const date = new Date(startDateStr + 'T12:00:00');
      let added = 0;
      while (added < daysToAdd) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) {
          added++;
        }
      }
      return date;
    };

    const formatDateSpanish = (d: Date): string => {
      const months = ['ene.', 'feb.', 'mar.', 'abr.', 'may.', 'jun.', 'jul.', 'ago.', 'sep.', 'oct.', 'nov.', 'dic.'];
      return `${d.getDate()} de ${months[d.getMonth()]}`;
    };

    try {
      const minDate = addBusinessDays(start, extDays + minDays + pickupExtraDay);
      const maxDate = addBusinessDays(start, extDays + maxDays + pickupExtraDay);
      return `Entrega estimada: ${formatDateSpanish(minDate)} - ${formatDateSpanish(maxDate)}`;
    } catch {
      return "El tiempo de entrega final puede ajustarse a la jornada de los couriers.";
    }
  };

  // NOTE: this effect must be declared AFTER dynamicTotal and currentSelectedRate are defined
  useEffect(() => {
    if (currentStep === 5 && paymentMethod === "card" && !clientSecret && dynamicTotal > 0) {
      fetchPaymentIntent(dynamicTotal, currentSelectedRate?.currency || "USD");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, paymentMethod, clientSecret, dynamicTotal, currentSelectedRate]);

  const isStep1Valid = React.useMemo(() => {
    const required = ["weight", "width", "height", "length", "fob", "packageContent"];
    const baseValid = required.every(
      (field) =>
        values[field as keyof typeof values] && !errors[field as keyof typeof errors],
    ) && disclaimerChecked;

    if (Number(values.fob) >= 250) {
      return baseValid && selectedDocument !== null;
    }
    return baseValid;
  }, [values, errors, disclaimerChecked, selectedDocument]);

  const isServiceTypeAvailable = React.useMemo(() => {
    if (!values.destination) return true;
    const country = receiverCountries.find((c) => c.iso2Country === values.destination);
    if (!country) return true;
    if (values.serviceType === "A") return !!country.air;
    if (values.serviceType === "M") return !!country.maritime;
    return true;
  }, [values.destination, values.serviceType, receiverCountries]);

  const isStep2Valid = React.useMemo(() => {
    const required = [
      "originAddressLine1", "originCity", "originState", "originZip",
      "destinationAddressLine1", "destinationCity", "destinationState", "destinationZip",
    ];
    const hasReceiverData =
      values.receiverFirstName && values.receiverLastName &&
      values.receiverEmail && values.receiverPhone &&
      values.receiverKindOfDNI && values.receiverDNI;
    return (
      required.every(
        (field) => values[field as keyof typeof values] && !errors[field as keyof typeof errors],
      ) &&
      !!hasReceiverData &&
      isServiceTypeAvailable
    );
  }, [values, errors, isServiceTypeAvailable]);

  const sortedAddresses = React.useMemo(() => {
    if (!Array.isArray(addresses)) return [];
    return [...addresses].sort((a, b) => {
      if (b.isDefault && !a.isDefault) return 1;
      if (!b.isDefault && a.isDefault) return -1;
      return (b.id || 0) - (a.id || 0);
    });
  }, [addresses]);

  const usaAddresses = sortedAddresses.filter(
    (addr) => (addr.isO2Country || "").toUpperCase() === (values.origin || "US").toUpperCase() && addr.type === "O"
  );
  const destinationAddresses = sortedAddresses.filter(
    (addr) => (addr.isO2Country || "").toUpperCase() === (values.destination || "").toUpperCase() && addr.type === "D"
  );
  const isOriginLimitReached = usaAddresses.length >= 5;
  const isDestinationLimitReached = destinationAddresses.length >= 5;

  // --- Helpers ---
  const fetchMunicipality = async (city: string, state: string, zip: string, country: string) => {
    if (!isLoaded || !window.google) return null;
    return new Promise<{ municipality: string; state: string } | null>((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: `${city}, ${state}, ${zip}, ${country}` }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          let municipality = "";
          let stateName = "";
          for (const component of results[0].address_components) {
            if (component.types.includes("administrative_area_level_2")) municipality = component.long_name;
            if (component.types.includes("administrative_area_level_1")) stateName = component.long_name;
          }
          resolve({ municipality, state: stateName });
        } else {
          resolve(null);
        }
      });
    });
  };

  const validateZip = async (type: "origin" | "destination", zip: string, country: string) => {
    if (!zip || !country) {
      if (type === "origin") { setOriginZipError(null); setOriginZipValidated(true); }
      else { setDestZipError(null); setDestZipValidated(true); }
      return;
    }
    if (type === "origin") setIsValidatingOriginZip(true);
    else setIsValidatingDestZip(true);
    try {
      const location = await utilsService.getLocationByZipcode({ iso2code: country, zipcode: zip });
      if (type === "origin") {
        setOriginZipError(null);
        setOriginZipValidated(true);
        if (location) {
          setValues((prev: any) => {
            const newCity = location.city || prev.originCity;
            const newState = location.state || prev.originState;
            setErrors((prevErrors: any) => ({
              ...prevErrors,
              originCity: newCity ? undefined : prevErrors.originCity,
              originState: newState ? undefined : prevErrors.originState,
            }));
            return {
              ...prev,
              originCity: newCity,
              originState: newState,
            };
          });
        }
      } else {
        setDestZipError(null);
        setDestZipValidated(true);
        if (location) {
          setValues((prev: any) => {
            const newCity = location.city || prev.destinationCity;
            const newState = location.state || prev.destinationState;
            setErrors((prevErrors: any) => ({
              ...prevErrors,
              destinationCity: newCity ? undefined : prevErrors.destinationCity,
              destinationState: newState ? undefined : prevErrors.destinationState,
            }));
            return {
              ...prev,
              destinationCity: newCity,
              destinationState: newState,
            };
          });
        }
      }
    } catch {
      if (type === "origin") { setOriginZipError(null); setOriginZipValidated(true); }
      else { setDestZipError(null); setDestZipValidated(true); }
    } finally {
      if (type === "origin") setIsValidatingOriginZip(false);
      else setIsValidatingDestZip(false);
    }
  };

  const getPayloadKey = (sourceValues: any) => {
    const pList = sourceValues.parcelOrdDoc === "DOC"
      ? [{ height: 0, width: 0, length: 0, weight: 1, fob: 0 }]
      : packages.map(pkg => ({
          height: Number(pkg.height) || 0,
          width: Number(pkg.width) || 0,
          length: Number(pkg.length) || 0,
          weight: Number(pkg.weight) || 0,
          fob: Number(pkg.fob) || 0,
          content: pkg.packageContent
        }));

    return JSON.stringify({
      origin: sourceValues.origin,
      destination: sourceValues.destination,
      parcelOrdDoc: sourceValues.parcelOrdDoc,
      currency: sourceValues.currency,
      unitSystem: sourceValues.unitSystem,
      originZip: sourceValues.originZip,
      destinationZip: sourceValues.destinationZip,
      pickupDate: sourceValues.pickupDate,
      packages: pList
    });
  };

  const handleCalculate = async (overrideValues?: any, overridePackages?: any) => {
    const src = overrideValues || values;
    lastCalculatedPayloadRef.current = getPayloadKey(src);

    const pkgsSource = overridePackages || packages;
    const mappedPackages = src.parcelOrdDoc === "DOC"
      ? [{ height: 0, width: 0, length: 0, weight: 1, fob: 0, content: src.packageContent, isFragile: false }]
      : pkgsSource.map((pkg: any) => ({
          height: Number(pkg.height) || 0,
          width: Number(pkg.width) || 0,
          length: Number(pkg.length) || 0,
          weight: Number(pkg.weight) || 0,
          fob: Number(pkg.fob) || 0,
          content: pkg.packageContent,
          isFragile: Boolean(pkg.isFragile)
        }));

    const payload: RatePayload = {
      iso2Origin: src.origin,
      iso2Destination: src.destination,
      serviceType: src.serviceType,
      height: mappedPackages[0]?.height || 0,
      width: mappedPackages[0]?.width || 0,
      length: mappedPackages[0]?.length || 0,
      weight: mappedPackages.reduce((acc: number, p: any) => acc + p.weight, 0),
      parcelOrdDoc: src.parcelOrdDoc,
      currency: src.currency,
      unitSystem: src.unitSystem === "metric" ? "METRIC" : "IMPERIAL",
      fob: mappedPackages.reduce((acc: number, p: any) => acc + (p.fob || 0), 0),
      zipcodeOrigin: src.originZip,
      zipcodeDestination: src.destinationZip,
      addressLineOrigin: src.originAddressLine1,
      cityOrigin: src.originCity,
      stateOrigin: src.originState,
      stateDestiny: src.destinationState,
      municipalityDestiny: src.municipalityDestiny || src.destinationCity,
      pickupDate: src.pickupDate,
      externalCarrierName: "UPS",
      packages: mappedPackages,
    };
    try {
      setIsCalculating(true);
      setShowResults(false);
      setErrors({});
      const results = await rateService.calculateRate(payload);
      const sortedResults = rateService.sortByCheapest(results);
      setRateResults(sortedResults);

      if (src.origin === src.destination && sortedResults.length > 0) {
        const nationalRate = sortedResults[0];
        const sortedServices = [...(nationalRate.services || [])].sort((a, b) => a.serviceSubTotal - b.serviceSubTotal);
        const stillAvailableService = sortedServices.find(s => s.idCarrierService === src.idCarrierService);
        const selectedCarrierService = stillAvailableService ? stillAvailableService.idCarrierService : (sortedServices[0]?.idCarrierService || null);
        setValues((prev: any) => ({
          ...prev,
          selectedService: nationalRate.carrierName,
          idCarrier: nationalRate.idCarrier || 1,
          idCarrierService: selectedCarrierService,
        }));
        if (nationalRate.externalServices && nationalRate.externalServices.length > 0) {
          setExternalServices(nationalRate.externalServices as any);
        }
        const hasPickupService = !!(nationalRate.externalServices?.[0]?.pickUpService);
        const hasDispatcherService = !!(nationalRate.externalServices?.[0]?.elpaketDispatcherService);

        if (userWantsElpaketDispatcherRef.current && hasDispatcherService) {
          setElpaketDispatcherSelected(true);
          setPickupSelected(false);
        } else if (userWantsPickupRef.current && hasPickupService) {
          setPickupSelected(true);
          setElpaketDispatcherSelected(false);
        } else {
          setPickupSelected(false);
          setElpaketDispatcherSelected(false);
        }
      } else if (sortedResults.length > 0) {
        const stillAvailable = sortedResults.find(
          (r) => r.carrierName === src.selectedService &&
                 (src.idCarrierService ? r.idCarrierService === src.idCarrierService : true)
        );
        const autoSelected = stillAvailable || sortedResults[0];
        const updatedVals = {
          ...src,
          selectedService: autoSelected.carrierName,
          idCarrier: autoSelected.idCarrier || src.idCarrier,
          idCarrierService: autoSelected.idCarrierService !== undefined ? autoSelected.idCarrierService : null,
        };
        setValues((prev: any) => ({
          ...prev,
          ...updatedVals,
        }));
        await fetchExternalRatesHelper(updatedVals);
      } else {
        setValues((prev: any) => ({ ...prev, selectedService: "" }));
      }
      setShowResults(true);
      return true;
    } catch (error) {
      console.error("Error calculating rates:", error);
      setRateResults([]);
      errorService.showErrorAlert(error, "Error");
      return false;
    } finally {
      setIsCalculating(false);
    }
  };

  useEffect(() => {
    if (currentStep === 3) {
      handleCalculate();
    }
  }, [currentStep]);

  const handleSaveOriginAddress = async (selectedAddress: Address) => {
    const prevService = values.selectedService;
    const prevCarrierService = values.idCarrierService;
    const updatedValues = {
      ...values,
      originZip: selectedAddress.zipCode,
      originNickname: selectedAddress.nickname || "",
      originAddressLine1: selectedAddress.addressLine1,
      originAddressLine2: selectedAddress.addressLine2 || "",
      originCity: selectedAddress.city,
      originState: selectedAddress.state,
      originPhone: selectedAddress.addressSubjet?.phoneNumber || values.originPhone,
      originFirstName: selectedAddress.addressSubjet?.firstName || values.originFirstName,
      originLastName: selectedAddress.addressSubjet?.lastName || values.originLastName,
      originEmail: selectedAddress.addressSubjet?.email || values.originEmail,
      originKindOfDNI: selectedAddress.addressSubjet?.kindOfDNI || values.originKindOfDNI,
      originDNI: selectedAddress.addressSubjet?.dni || values.originDNI,
    };
    setSelectedOriginId(selectedAddress.id);
    setValues(updatedValues);
    setOriginZipValidated(true);
    setOriginZipError(null);
    const success = await handleCalculate(updatedValues);
    if (success) {
      setRateResults((prevResults) => {
        const stillAvailable = prevResults.find(
          (r) => r.carrierName === prevService && r.idCarrierService === prevCarrierService,
        );
        if (stillAvailable) {
          setValues((v) => ({ ...v, selectedService: prevService, idCarrierService: prevCarrierService }));
        }
        return prevResults;
      });
    }
  };

  const handleSaveDestAddress = async (selectedAddress: Address) => {
    const prevService = values.selectedService;
    const prevCarrierService = values.idCarrierService;
    const updatedValues = {
      ...values,
      destination: selectedAddress.isO2Country || values.destination,
      destinationZip: selectedAddress.zipCode,
      destinationNickname: selectedAddress.nickname || "",
      destinationAddressLine1: selectedAddress.addressLine1,
      destinationAddressLine2: selectedAddress.addressLine2 || "",
      destinationCity: selectedAddress.city,
      destinationState: selectedAddress.state,
      receiverFirstName: selectedAddress.addressSubjet?.firstName || "",
      receiverLastName: selectedAddress.addressSubjet?.lastName || "",
      receiverEmail: selectedAddress.addressSubjet?.email || "",
      receiverPhone: selectedAddress.addressSubjet?.phoneNumber || "",
      receiverKindOfDNI: selectedAddress.addressSubjet?.kindOfDNI || "",
      receiverDNI: selectedAddress.addressSubjet?.dni || "",
      municipalityDestiny: selectedAddress.city,
      stateDestiny: selectedAddress.state,
    };
    setSelectedDestId(selectedAddress.id);
    setValues(updatedValues);
    setDestZipValidated(true);
    setDestZipError(null);
    const googleLocation = await fetchMunicipality(
      selectedAddress.city, selectedAddress.state, selectedAddress.zipCode,
      selectedAddress.isO2Country || values.destination,
    );
    if (googleLocation) {
      updatedValues.municipalityDestiny = googleLocation.municipality;
      updatedValues.stateDestiny = googleLocation.state;
      setValues((v: any) => ({ ...v, municipalityDestiny: googleLocation.municipality, stateDestiny: googleLocation.state }));
    }
    const success = await handleCalculate(updatedValues);
    if (success) {
      setRateResults((prevResults) => {
        const stillAvailable = prevResults.find(
          (r) => r.carrierName === prevService && r.idCarrierService === prevCarrierService,
        );
        if (stillAvailable) {
          setValues((v) => ({ ...v, selectedService: prevService, idCarrierService: prevCarrierService }));
        }
        return prevResults;
      });
    }
  };

  const handleDeleteAddress = async (addressId: number) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta dirección se eliminará de forma permanente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const response = await customerAddressService.deleteAddress(addressId);
        if (response.success) {
          Swal.fire("Eliminada", "La dirección ha sido eliminada.", "success");
          const refreshedAddresses = await customerAddressService.getAddresses();
          setAddresses(refreshedAddresses.all || []);
          if (selectedOriginId === addressId) {
            setSelectedOriginId(null);
            setValues((prev: any) => ({
              ...prev,
              originZip: "",
              originNickname: "",
              originAddressLine1: "",
              originAddressLine2: "",
              originCity: "",
              originState: "",
              originPhone: "",
              originFirstName: "",
              originLastName: "",
              originEmail: "",
              originKindOfDNI: "",
              originDNI: "",
            }));
          }
          if (selectedDestId === addressId) {
            setSelectedDestId(null);
            setValues((prev: any) => ({
              ...prev,
              destinationZip: "",
              destinationNickname: "",
              destinationAddressLine1: "",
              destinationAddressLine2: "",
              destinationCity: "",
              destinationState: "",
              receiverFirstName: "",
              receiverLastName: "",
              receiverEmail: "",
              receiverPhone: "",
              receiverKindOfDNI: "",
              receiverDNI: "",
              municipalityDestiny: "",
              stateDestiny: "",
            }));
          }
        } else {
          Swal.fire("Error", response.error || "No se pudo eliminar la dirección.", "error");
        }
      } catch (error) {
        Swal.fire("Error", "Ocurrió un error al intentar eliminar la dirección.", "error");
      }
    }
  };

  const handleEditAddressClick = (addr: Address) => {
    setAddressToEdit(addr);
    setShowEditAddressModal(true);
  };

  const handleAddressUpdated = async () => {
    setShowEditAddressModal(false);
    setAddressToEdit(null);
    const refreshed = await customerAddressService.getAddresses();
    const allRefreshed = refreshed.all || [];
    setAddresses(allRefreshed);
    
    if (selectedOriginId && addressToEdit?.id === selectedOriginId) {
      const updatedAddr = allRefreshed.find(a => a.id === selectedOriginId);
      if (updatedAddr) {
        setValues((prev: any) => ({
          ...prev,
          originZip: updatedAddr.zipCode,
          originNickname: updatedAddr.nickname,
          originAddressLine1: updatedAddr.addressLine1,
          originAddressLine2: updatedAddr.addressLine2 || "",
          originCity: updatedAddr.city,
          originState: updatedAddr.state,
          originFirstName: updatedAddr.addressSubjet?.firstName || "",
          originLastName: updatedAddr.addressSubjet?.lastName || "",
          originPhone: updatedAddr.addressSubjet?.phoneNumber || "",
          originEmail: updatedAddr.addressSubjet?.email || "",
          originKindOfDNI: updatedAddr.addressSubjet?.kindOfDNI || "",
          originDNI: updatedAddr.addressSubjet?.dni || "",
        }));
        validateZip("origin", updatedAddr.zipCode, "US");
      }
    } else if (selectedDestId && addressToEdit?.id === selectedDestId) {
      const updatedAddr = allRefreshed.find(a => a.id === selectedDestId);
      if (updatedAddr) {
        setValues((prev: any) => ({
          ...prev,
          destinationZip: updatedAddr.zipCode,
          destinationNickname: updatedAddr.nickname,
          destinationAddressLine1: updatedAddr.addressLine1,
          destinationAddressLine2: updatedAddr.addressLine2 || "",
          destinationCity: updatedAddr.city,
          destinationState: updatedAddr.state,
          receiverFirstName: updatedAddr.addressSubjet?.firstName || "",
          receiverLastName: updatedAddr.addressSubjet?.lastName || "",
          receiverEmail: updatedAddr.addressSubjet?.email || "",
          receiverPhone: updatedAddr.addressSubjet?.phoneNumber || "",
          receiverKindOfDNI: updatedAddr.addressSubjet?.kindOfDNI || "",
          receiverDNI: updatedAddr.addressSubjet?.dni || "",
          municipalityDestiny: updatedAddr.city,
          stateDestiny: updatedAddr.state,
        }));
        fetchMunicipality(updatedAddr.city, updatedAddr.state, updatedAddr.zipCode, values.destination);
        validateZip("destination", updatedAddr.zipCode, values.destination);
      }
    }
  };

  const handleSavePackageData = async (updatedData: any) => {
    const prevService = values.selectedService;
    const prevCarrierService = values.idCarrierService;
    const updatedValues = { ...values, ...updatedData };
    
    let pkgsToSave = packages;
    if (updatedData.packages && updatedData.packages.length > 1) {
      const sumWeight = updatedData.packages.reduce((acc: number, p: any) => acc + (Number(p.weight) || 0), 0);
      const sumFob = updatedData.packages.reduce((acc: number, p: any) => acc + (Number(p.fob) || 0), 0);
      updatedValues.weight = sumWeight;
      updatedValues.fob = sumFob;
      updatedValues.height = Number(updatedData.packages[0]?.height) || 0;
      updatedValues.width = Number(updatedData.packages[0]?.width) || 0;
      updatedValues.length = Number(updatedData.packages[0]?.length) || 0;
      pkgsToSave = updatedData.packages;
      setPackages(updatedData.packages);
    } else {
      pkgsToSave = [{
        length: String(updatedData.length),
        width: String(updatedData.width),
        height: String(updatedData.height),
        weight: String(updatedData.weight),
        fob: String(updatedData.fob),
        packageContent: updatedData.packageContent || ""
      }];
      setPackages(pkgsToSave);
    }
    setValues(updatedValues);
    if (updatedData.selectedDocument !== undefined) {
      setSelectedDocument(updatedData.selectedDocument);
    }
    const success = await handleCalculate(updatedValues, pkgsToSave);
    if (success) {
      setRateResults((prevResults) => {
        const stillAvailable = prevResults.find(
          (r) => r.carrierName === prevService && r.idCarrierService === prevCarrierService,
        );
        if (stillAvailable) {
          setValues((v) => ({ ...v, selectedService: prevService, idCarrierService: prevCarrierService }));
        }
        return prevResults;
      });
    }
  };

  const validateStep = (step: number): boolean => {
    let stepFields: string[] = [];
    let isStepValid = true;

    if (step === 1) {
      if (!disclaimerChecked) {
        isStepValid = false;
        setDisclaimerError(true);
      } else {
        setDisclaimerError(false);
      }

      if (values.parcelOrdDoc === "DOC") {
        stepFields = ["weight", "fob", "packageContent"];
      } else {
        let allPkgsValid = true;
        const validatedPackages = packages.map((pkg) => {
          const lErr = validateDimensionValue(pkg.length, "Largo", values.unitSystem);
          const wErr = validateDimensionValue(pkg.width, "Ancho", values.unitSystem);
          const hErr = validateDimensionValue(pkg.height, "Alto", values.unitSystem);
          const wtErr = validateWeightValue(pkg.weight, values.unitSystem);
          const fErr = !pkg.fob || Number(pkg.fob) < 0 ? "Requerido (>= 0)" : null;
          const cErr = !pkg.packageContent ? "Requerido" : null;

          if (lErr || wErr || hErr || wtErr || fErr || cErr) {
            allPkgsValid = false;
          }

          return {
            ...pkg,
            errors: {
              length: lErr,
              width: wErr,
              height: hErr,
              weight: wtErr,
              fob: fErr,
              packageContent: cErr,
            }
          };
        });
        setPackages(validatedPackages);
        if (!allPkgsValid) {
          isStepValid = false;
        }
      }
    }

    const newErrors = { ...errors } as any;
    let firstFailedField: string | null = null;

    stepFields.forEach((field) => {
      const rule = (validationRules as any)[field];
      if (rule) {
        const error = rule((values as any)[field], values);
        if (error) {
          newErrors[field] = error;
          isStepValid = false;
          if (!firstFailedField) {
            firstFailedField = field;
          }
        } else {
          newErrors[field] = null;
        }
      }
    });
    setErrors(newErrors);

    if (!isStepValid) {
      setTimeout(() => {
        if (firstFailedField) {
          let element = document.getElementsByName(firstFailedField)[0] as HTMLElement;
          if (!element) {
            element = document.getElementById(firstFailedField) as HTMLElement;
          }
          if (element) {
            element.focus();
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }
        }
        if (step === 1 && !disclaimerChecked) {
          const element = document.getElementById("disclaimer-checkbox-container");
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }
        }
        const firstErrorEl = document.querySelector(
          "[class*='isInvalid'], [class*='invalidFeedback'], .is-invalid, .invalid-feedback"
        ) as HTMLElement;
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 50);
    }

    return isStepValid;
  };

  const confirmDeliveryMethod = async (): Promise<boolean> => {
    const pickupSvc = externalServices[0]?.pickUpService;
    const elpaketSvc = externalServices[0]?.elpaketDispatcherService;
    const altSvc = elpaketSvc || pickupSvc;
    if (!altSvc) return true;
    const currency = currentSelectedRate?.currency || "USD";
    const price = `$${altSvc.serviceSubTotal.toFixed(2)} ${currency}`;
    const pickupChosen = pickupSelected || elpaketDispatcherSelected;
    const pickupAgent = (elpaketDispatcherSelected || (!pickupChosen && elpaketSvc))
      ? "nuestro transportista ElPaket"
      : "un chofer oficial UPS";

    let formattedPickupDate = "";
    if (pickupChosen && values.pickupDate) {
      try {
        const [y, m, d] = values.pickupDate.split("-");
        const pickupDateObj = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10), 12, 0, 0);
        const days = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
        const months = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
        formattedPickupDate = `${days[pickupDateObj.getDay()]}, ${pickupDateObj.getDate()} de ${months[pickupDateObj.getMonth()]} de ${pickupDateObj.getFullYear()}`;
      } catch (e) {
        formattedPickupDate = values.pickupDate;
      }
    }

    const result = await Swal.fire({
      title: "Confirme su opción de envío",
      html: pickupChosen
        ? `Ha seleccionado el servicio de <b>Pickup</b>.<br/>${pickupAgent === "nuestro transportista ElPaket" ? "Nuestro transportista ElPaket" : "Un conductor autorizado de UPS"} recogerá su paquete en su casa u oficina${formattedPickupDate ? ` el <b>${formattedPickupDate}</b>` : ""}${elpaketDispatcherSelected ? "" : " entre las 8:00 a. m. y las 05:00 p. m."}.<br/><br/>También puede llevar su paquete a la tienda UPS de su preferencia (<b>Drop Off</b>) y <b>ahorrar ${price}</b>.`
        : `Ha seleccionado la modalidad <b>Drop Off</b>.<br/>Puede llevar su paquete a la tienda UPS más cercana de su preferencia <b>sin costo adicional</b>.<br/><br/>También puede solicitar el servicio de <b>Pickup</b> para que ${pickupAgent === "nuestro transportista ElPaket" ? "nuestro transportista ElPaket" : "un conductor autorizado de UPS"} recoja su paquete en la comodidad de su casa u oficina.<br/>Cambie su opción a Pickup para consultar la tarifa disponible para su envío.`,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cambiar opción",
    });
    return result.isConfirmed;
  };

  const handleNextStep = async () => {
    if (currentStep === 1) {
      if (validateStep(1)) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      if (!selectedOriginId) {
        Swal.fire({ icon: "warning", title: "Atención", text: "Debes seleccionar una dirección de origen." });
        return;
      }
      if (!selectedDestId) {
        Swal.fire({ icon: "warning", title: "Atención", text: "Debes seleccionar una dirección de destino." });
        return;
      }
      const currentKey = getPayloadKey(values);
      if (rateResults.length === 0 || lastCalculatedPayloadRef.current !== currentKey) {
        const success = await handleCalculate();
        if (success) setCurrentStep(3);
      } else {
        setCurrentStep(3);
      }
    } else if (currentStep === 3) {
      const confirmed = await confirmDeliveryMethod();
      if (!confirmed) return;
      setCurrentStep(4);
    } else if (currentStep === 4) {
      setCurrentStep(5);
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBackStep = () => setCurrentStep((prev) => prev - 1);

  const fetchPaymentIntent = async (amount: number, currency: string) => {
    try {
      setLoadingPayment(true);
      const customerData = tokenService.getCustomerDataToken()?.customerData;
      const email = customerData?.Email || "";
      const description = `Envío a ${values.receiverFirstName} ${values.receiverLastName} - ${values.destinationCity}`;
      const response = await paymentService.createPaymentIntent(amount, currency, description, email);
      if (response && response.clientSecret) {
        setClientSecret(response.clientSecret);
        setPaymentIntentId(response.id);
      } else {
        Swal.fire({ icon: "error", title: "Error", text: "No se pudo inicializar el pago." });
      }
    } catch (error) {
      console.error("Error fetching payment intent:", error);
    } finally {
      setLoadingPayment(false);
    }
  };

  const executeShipmentCreation = async (paymentMethodId?: string, confirmedIntentId?: string) => {
    setIsProcessingOrder(true);
    const decodedToken = tokenService.getCustomerDataToken();
    if (!decodedToken) {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo obtener la información de sesión." });
      setIsProcessingOrder(false);
      return;
    }
    const customer = decodedToken.customerData;
    const originAddr = addresses.find((a) => a.id === selectedOriginId);
    const finalOriginPhone = originAddr?.addressSubjet?.phoneNumber || values.originPhone || customer.PhoneNumber;

    const isNational = values.origin === values.destination;

    const mappedPackages = values.parcelOrdDoc === "DOC"
      ? [{ height: 0, width: 0, length: 0, weight: 1, fob: 0, content: values.packageContent, isFragile: false }]
      : packages.map(pkg => ({
          height: Number(pkg.height) || 0,
          width: Number(pkg.width) || 0,
          length: Number(pkg.length) || 0,
          weight: Number(pkg.weight) || 0,
          fob: Number(pkg.fob) || 0,
          content: pkg.packageContent,
          isFragile: Boolean(pkg.isFragile)
        }));

    const payload: CreateOrderRequest = {
      idCarrier: values.idCarrier,
      idCarrierService: values.idCarrierService || null,
      senderDetails: {
        firstName: values.originFirstName,
        lastName: values.originLastName,
        email: values.originEmail || customer.Email,
        PhoneNumber: finalOriginPhone,
        KindOfDNI: values.originKindOfDNI || customer.KindOfDNI,
        DNI: values.originDNI || customer.DNI,
        AddressLine1: values.originAddressLine1,
        AddressLine2: values.originAddressLine2,
        City: values.originCity,
        State: values.originState,
        Municipality: values.originCity,
        ZipCode: values.originZip,
        iso2Country: values.origin,
        isThirdPerson: !!originAddr?.addressSubjet?.isThirdPerson,
      },
      receiverDetails: {
        firstName: values.receiverFirstName,
        lastName: values.receiverLastName,
        email: values.receiverEmail,
        PhoneNumber: values.receiverPhone,
        KindOfDNI: values.receiverKindOfDNI,
        DNI: values.receiverDNI,
        AddressLine1: values.destinationAddressLine1,
        AddressLine2: values.destinationAddressLine2,
        City: values.destinationCity,
        State: values.destinationState,
        Municipality: values.municipalityDestiny || values.destinationCity,
        ZipCode: values.destinationZip,
        iso2Country: values.destination,
        isThirdPerson: false,
      },
      parcelData: {
        serviceType: values.serviceType,
        height: mappedPackages[0]?.height || 0,
        width: mappedPackages[0]?.width || 0,
        length: mappedPackages[0]?.length || 0,
        weight: mappedPackages.reduce((acc, p) => acc + p.weight, 0),
        parcelOrdDoc: values.parcelOrdDoc,
        currency: values.currency,
        unitSystem: values.unitSystem.toUpperCase(),
        fob: mappedPackages.reduce((acc, p) => acc + (p.fob || 0), 0),
        content: mappedPackages.map(p => p.content).filter(Boolean).join(", ") || values.packageContent,
        isFragile: values.parcelOrdDoc === "DOC" ? false : mappedPackages.some(p => p.isFragile),
      },
      idExternalCourier: isNational ? null : (elpaketDispatcherSelected ? null : (externalServices[0]?.idExternalCourier || null)),
      idExternalCourierService: isNational ? null : (elpaketDispatcherSelected ? null : (selectedExternalOption?.idExternalCourierService || null)),
      idExternalAdditionalService: elpaketDispatcherSelected
        ? (externalServices[0]?.elpaketDispatcherService ? externalServices[0].elpaketDispatcherService.idExternalCourierService : null)
        : ((pickupSelected && externalServices[0]?.pickUpService) ? externalServices[0].pickUpService.idExternalCourierService : null),
      paymentMethodId: paymentMethodId,
      pickupDate: values.pickupDate || undefined,
      hasBattery: values.hasBattery,
      DocumentoBase64: selectedDocument ? selectedDocument.base64string : undefined,
      DocumentoExt: selectedDocument ? selectedDocument.ext : undefined,
      packages: mappedPackages,
    };

    try {
      const response = await orderService.createOrder(payload);
      if (response.statusCode === 200) {
        setOrderSuccessData({
          ...response.data,
          idOrder: response.orderId || response.data?.id || response.data?.idOrder
        });
        setShowResults(false);
      } else if (response.statusCode === 202 && response.clientSecret) {
        setAllowRedirect(true);
        const stripe = await stripePromise;
        if (!stripe) throw new Error("Stripe context not found");
        const { error, paymentIntent } = await stripe.handleNextAction({ clientSecret: response.clientSecret });
        if (error) {
          Swal.fire({ icon: "error", title: "Error de Verificación", text: error.message || "La verificación del pago falló." });
        } else if (paymentIntent && paymentIntent.status === "succeeded") {
          await executeShipmentCreation(paymentMethodId, paymentIntent.id);
        } else {
          Swal.fire({ icon: "warning", title: "Acción Pendiente", text: "El pago aún requiere atención o fue cancelado." });
        }
      } else {
        errorService.showErrorAlert(response, "Error");
      }
    } catch (error) {
      console.error("Error in executeShipmentCreation:", error);
      errorService.showErrorAlert(error, "Error");
    } finally {
      setIsProcessingOrder(false);
    }
  };

  const resetForm = () => {
    if (orderNumberParam) navigate("/customer/send-package", { replace: true });
    setValues(initialValues);
    setOrderSuccessData(null);
    setCurrentStep(1);
    setSelectedOriginId(null);
    setSelectedDestId(null);
    setRateResults([]);
    setSelectedExternalIndicesByCarrier(new Map());
    setErrors({});
    setOriginZipValidated(false);
    setDestZipValidated(false);
    setOriginZipError(null);
    setDestZipError(null);
    setClientSecret(null);
    setPaymentIntentId(null);
    setLoadingPayment(false);
    setIsProcessingOrder(false);
    setShowResults(false);
    setDisclaimerChecked(false);
    setDisclaimerError(false);
  };

  const STEPS = [
    { step: 1, label: "Paquete", icon: faBox },
    { step: 2, label: "Dirección", icon: faMapMarkerAlt },
    { step: 3, label: "Servicio", icon: faTruck },
    { step: 4, label: "Resumen", icon: faCalculator },
    { step: 5, label: "Pago", icon: faCreditCard },
  ];

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <DashboardLayout>
      <div className={styles.main}>
        {/* ---- Step Indicator ---- */}
        {!orderSuccessData && (
          <div className={styles.indicatorCard}>
            <div className={styles.stepIndicator}>
              {STEPS.map((s) => (
                <div
                  key={s.step}
                  className={`${styles.step} ${currentStep === s.step ? styles.active : ""} ${currentStep > s.step ? styles.completed : ""}`}
                >
                  <div className={styles.stepNumber}>
                    {currentStep > s.step
                      ? <FontAwesomeIcon icon={faCheckCircle} />
                      : <FontAwesomeIcon icon={s.icon} />}
                  </div>
                  <span className={styles.stepLabel}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---- Content Card ---- */}
        <div className={styles.contentCard}>
          {orderSuccessData ? (
            (() => {
              const upsTrackingNumber = orderSuccessData.isFromResume
                ? orderSuccessData.additionalService?.serviceName?.toUpperCase() === "UPS"
                  ? orderSuccessData.additionalService?.externalReference
                  : undefined
                : orderSuccessData.externalService?.find(
                    (s: any) => s.providerName?.toUpperCase() === "UPS" || s.serviceName?.toUpperCase() === "UPS",
                  )?.serviceTrackingNumber;
              return (
                <OrderSuccessCard
                  trackingNumber={orderSuccessData.trackingNumber}
                  upsTrackingNumber={upsTrackingNumber}
                  idOrder={orderSuccessData.id || orderSuccessData.idOrder}
                  carrierName={orderSuccessData.carrier?.carrierName || orderSuccessData.carrierName || "Envío"}
                  carrierBase64={orderSuccessData.carrier?.carrierBase64 || orderSuccessData.base64Carrier}
                  externalServiceBase64={orderSuccessData.externalService?.[0]?.serviceBase64 || orderSuccessData.external?.base64}
                  externalServices={orderSuccessData.externalService}
                  base64Pdf={orderSuccessData.base64Pdf}
                  receiverName={`${orderSuccessData.receiverFirstName || values.receiverFirstName} ${orderSuccessData.receiverLastName || values.receiverLastName}`}
                  destination={`${orderSuccessData.destinationCity || values.destinationCity}, ${orderSuccessData.destinationState || values.destinationState}`}
                  weight={`${orderSuccessData.weight || values.weight} ${orderSuccessData.weightUnit || (values.unitSystem === "metric" ? "kg" : "lbs")}`}
                  content={orderSuccessData.packageContent || values.packageContent}
                  hasSecondaryLabel={orderSuccessData.hasSecondaryLabel || (orderSuccessData.externalService && orderSuccessData.externalService.length > 0)}
                  onNewShipment={resetForm}
                />
              );
            })()
          ) : (
            <>
              {/* ================================================================
                  PASO 1 – Información del Paquete (igual a PickUpPage)
              ================================================================ */}
              {currentStep === 1 && (
                <div className={styles.fadeIn}>
                  <div className={styles.sectionTitleRow}>
                    <h4 className={`${styles.sectionTitle} ${styles.m0}`}>Datos del Paquete</h4>
                    {/* Unit toggle */}
                    <div className={styles.unitToggle} style={{ margin: 0 }}>
                      <button
                        className={`${styles.toggleBtn} ${values.unitSystem === "imperial" ? styles.active : ""}`}
                        onClick={() => {
                          setValues((prev: any) => ({
                            ...prev,
                            unitSystem: "imperial",
                            width: "",
                            height: "",
                            length: "",
                            weight: "",
                          }));
                          setErrors((prev: any) => ({
                            ...prev,
                            width: undefined,
                            height: undefined,
                            length: undefined,
                            weight: undefined,
                          }));
                          setDisclaimerError(false);
                        }}
                      >
                        Imperial (lb/in)
                      </button>
                      <button
                        className={`${styles.toggleBtn} ${values.unitSystem === "metric" ? styles.active : ""}`}
                        onClick={() => {
                          setValues((prev: any) => ({
                            ...prev,
                            unitSystem: "metric",
                            width: "",
                            height: "",
                            length: "",
                            weight: "",
                          }));
                          setErrors((prev: any) => ({
                            ...prev,
                            width: undefined,
                            height: undefined,
                            length: undefined,
                            weight: undefined,
                          }));
                          setDisclaimerError(false);
                        }}
                      >
                        Métrico (kg/cm)
                      </button>
                    </div>
                  </div>

                  {/* Package info grid: box SVG + inputs */}
                  <div className={styles.packageInfoGrid}>
                    <div className={styles.stepImageCentered}>
                      <BoxIllustration className={styles.stepImageLarge} />
                    </div>

                    <div className={styles.packageInfoInputs}>
                      {/* Tipo de Paquete */}
                      <div className={styles.formGroup}>
                        <label>Tipo de Paquete</label>
                        <div className={styles.checkboxOptionRow}>
                          <div
                            className={`${styles.checkboxOption} ${values.parcelOrdDoc === "PARCEL" ? styles.selected : ""}`}
                            onClick={() => {
                              setValues((prev: any) => ({
                                ...prev,
                                parcelOrdDoc: "PARCEL",
                                width: "",
                                height: "",
                                length: "",
                                weight: "",
                                fob: "",
                                packageContent: "",
                                hasBattery: false,
                              }));
                              setSelectedDocument(null);
                              setDisclaimerChecked(false);
                              setErrors({});
                              setDisclaimerError(false);
                            }}
                            style={{ outline: "none", WebkitTapHighlightColor: "transparent" }}
                          >
                            <input type="checkbox" checked={values.parcelOrdDoc === "PARCEL"} readOnly style={{ outline: "none" }} />
                            <span>Caja</span>
                          </div>
                          <div
                            className={`${styles.checkboxOption} ${values.parcelOrdDoc === "DOC" ? styles.selected : ""}`}
                            onClick={() => {
                              setValues((prev: any) => ({
                                ...prev,
                                parcelOrdDoc: "DOC",
                                width: "",
                                height: "",
                                length: "",
                                weight: "",
                                fob: "",
                                packageContent: "",
                                hasBattery: false,
                              }));
                              setSelectedDocument(null);
                              setDisclaimerChecked(false);
                              setErrors({});
                              setDisclaimerError(false);
                            }}
                            style={{ outline: "none", WebkitTapHighlightColor: "transparent" }}
                          >
                            <input type="checkbox" checked={values.parcelOrdDoc === "DOC"} readOnly style={{ outline: "none" }} />
                            <span>Documento</span>
                          </div>
                        </div>
                      </div>

                      {values.parcelOrdDoc === "DOC" ? (
                        <>
                          {/* Dimensiones */}
                          <div className={styles.grid3Col} style={{ marginTop: '20px' }}>
                            <div className={styles.formGroup}>
                              <label>Largo ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                              <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} name="length" value={values.length} onChange={handleChange}
                                className={errors.length ? styles.isInvalid : ""} placeholder="0" />
                              {errors.length && <span className={styles.invalidFeedback}>{errors.length}</span>}
                            </div>
                            <div className={styles.formGroup}>
                              <label>Ancho ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                              <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} name="width" value={values.width} onChange={handleChange}
                                className={errors.width ? styles.isInvalid : ""} placeholder="0" />
                              {errors.width && <span className={styles.invalidFeedback}>{errors.width}</span>}
                            </div>
                            <div className={styles.formGroup}>
                              <label>Alto ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                              <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} name="height" value={values.height} onChange={handleChange}
                                className={errors.height ? styles.isInvalid : ""} placeholder="0" />
                              {errors.height && <span className={styles.invalidFeedback}>{errors.height}</span>}
                            </div>
                          </div>

                          {/* Peso + FOB */}
                          <div className={styles.grid2Col} style={{ marginTop: '15px' }}>
                            <div className={styles.formGroup}>
                              <label>Peso ({values.unitSystem === "metric" ? "kg" : "lb"})</label>
                              <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} name="weight" value={values.weight} onChange={handleChange}
                                className={errors.weight ? styles.isInvalid : ""} placeholder="0.00" />
                              {errors.weight && <span className={styles.invalidFeedback}>{errors.weight}</span>}
                            </div>
                            <div className={styles.formGroup}>
                              <label>Valor FOB (USD)</label>
                              <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} name="fob" value={values.fob} onChange={handleChange}
                                className={errors.fob ? styles.isInvalid : ""} placeholder="0.00" />
                              {errors.fob && <span className={styles.invalidFeedback}>{errors.fob}</span>}
                            </div>
                          </div>

                          {/* Descripción del contenido */}
                          <div className={`${styles.formGroup} ${styles.mt15}`}>
                            <label>Descripción del Contenido</label>
                            <textarea name="packageContent" value={values.packageContent} onChange={handleChange}
                              className={errors.packageContent ? styles.isInvalid : ""}
                              placeholder="Ej: Ropa, repuestos, electrónicos..." rows={3} />
                            {errors.packageContent && <span className={styles.invalidFeedback}>{errors.packageContent}</span>}
                          </div>
                        </>
                      ) : (
                        <div style={{ marginTop: '20px' }}>
                          {packages.map((pkg, idx) => (
                            <div key={idx} style={{ border: '1px solid #cbd5e1', borderRadius: '12px', padding: '16px', marginBottom: '16px', backgroundColor: '#ffffff' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <span style={{ fontWeight: 600, fontSize: '13px', color: '#000000' }}>Pieza #{idx + 1}</span>
                                {packages.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemovePackage(idx)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#ef4444',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      fontWeight: 600,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    Eliminar
                                  </button>
                                )}
                              </div>

                              <div className={styles.grid3Col} style={{ marginBottom: '12px' }}>
                                <div className={styles.formGroup}>
                                  <label>Largo ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                                  <input
                                    type="number"
                                    min="0"
                                    onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                                    value={pkg.length}
                                    onChange={(e) => handlePackageChange(idx, 'length', e.target.value)}
                                    className={pkg.errors?.length ? styles.isInvalid : ""}
                                    placeholder="0"
                                  />
                                  {pkg.errors?.length && <span className={styles.invalidFeedback}>{pkg.errors.length}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                  <label>Ancho ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                                  <input
                                    type="number"
                                    min="0"
                                    onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                                    value={pkg.width}
                                    onChange={(e) => handlePackageChange(idx, 'width', e.target.value)}
                                    className={pkg.errors?.width ? styles.isInvalid : ""}
                                    placeholder="0"
                                  />
                                  {pkg.errors?.width && <span className={styles.invalidFeedback}>{pkg.errors.width}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                  <label>Alto ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                                  <input
                                    type="number"
                                    min="0"
                                    onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                                    value={pkg.height}
                                    onChange={(e) => handlePackageChange(idx, 'height', e.target.value)}
                                    className={pkg.errors?.height ? styles.isInvalid : ""}
                                    placeholder="0"
                                  />
                                  {pkg.errors?.height && <span className={styles.invalidFeedback}>{pkg.errors.height}</span>}
                                </div>
                              </div>

                              <div className={styles.grid2Col} style={{ marginBottom: '12px' }}>
                                <div className={styles.formGroup}>
                                  <label>Peso ({values.unitSystem === "metric" ? "kg" : "lb"})</label>
                                  <input
                                    type="number"
                                    min="0"
                                    onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                                    value={pkg.weight}
                                    onChange={(e) => handlePackageChange(idx, 'weight', e.target.value)}
                                    className={pkg.errors?.weight ? styles.isInvalid : ""}
                                    placeholder="0.00"
                                  />
                                  {pkg.errors?.weight && <span className={styles.invalidFeedback}>{pkg.errors.weight}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                  <label>Valor FOB (USD)</label>
                                  <input
                                    type="number"
                                    min="0"
                                    onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                                    value={pkg.fob}
                                    onChange={(e) => handlePackageChange(idx, 'fob', e.target.value)}
                                    className={pkg.errors?.fob ? styles.isInvalid : ""}
                                    placeholder="0.00"
                                  />
                                  {pkg.errors?.fob && <span className={styles.invalidFeedback}>{pkg.errors.fob}</span>}
                                </div>
                              </div>

                              <div className={styles.formGroup}>
                                <label>Descripción del Contenido</label>
                                <textarea
                                  value={pkg.packageContent}
                                  onChange={(e) => handlePackageChange(idx, 'packageContent', e.target.value)}
                                  className={pkg.errors?.packageContent ? styles.isInvalid : ""}
                                  placeholder="Ej: Ropa, repuestos, electrónicos..."
                                  rows={2}
                                />
                                {pkg.errors?.packageContent && <span className={styles.invalidFeedback}>{pkg.errors.packageContent}</span>}
                              </div>

                              <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                  type="checkbox"
                                  id={`fragile-pkg-${idx}`}
                                  checked={Boolean(pkg.isFragile)}
                                  onChange={(e) => handlePackageChange(idx, 'isFragile', e.target.checked)}
                                  style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#000000' }}
                                />
                                <label htmlFor={`fragile-pkg-${idx}`} style={{ fontSize: '0.85rem', fontWeight: 600, color: '#000000', cursor: 'pointer', margin: 0 }}>
                                  ¿Contenido Frágil?
                                </label>
                              </div>
                            </div>
                          ))}

                          {packages.length < 10 && (
                            <button
                              type="button"
                              onClick={handleAddPackage}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                padding: '10px 16px',
                                backgroundColor: '#f1f5f9',
                                color: '#334155',
                                border: '1.5px dashed #cbd5e1',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                width: '100%',
                                fontWeight: 600,
                                marginBottom: '20px',
                                outline: 'none'
                              }}
                            >
                              <FontAwesomeIcon icon={faPlus} /> Agregar otra pieza
                            </button>
                          )}
                        </div>
                      )}

                      <div id="doc-upload-container" className={`${styles.formGroup} ${styles.mt20}`} style={{ border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '16px', backgroundColor: '#f8fafc' }}>
                        <label style={{ fontWeight: 600, color: '#334155', display: 'block', marginBottom: '8px' }}>
                          Documentación de Respaldo (Opcional)
                        </label>
                          <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '12px' }}>
                            Cargue un archivo de respaldo (PNG/JPG máx 5MB, PDF máx 500KB). Si dispone de documentación de respaldo, se recomienda adjuntarla.
                          </p>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <label style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '8px 16px',
                              backgroundColor: '#0f172a',
                              color: '#ffffff',
                              borderRadius: '8px',
                              cursor: (selectedDocument || isCompressingDoc) ? 'not-allowed' : 'pointer',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              opacity: (selectedDocument || isCompressingDoc) ? 0.6 : 1,
                              userSelect: 'none',
                              transition: 'background-color 0.2s'
                            }}>
                              <FontAwesomeIcon icon={isCompressingDoc ? faSpinner : faPlus} spin={isCompressingDoc} style={{ marginRight: '6px' }} />
                              {isCompressingDoc ? "Procesando..." : "Seleccionar Archivo"}
                              <input
                                type="file"
                                accept=".png,.jpg,.jpeg,.pdf"
                                onChange={handleDocumentChange}
                                disabled={!!selectedDocument || isCompressingDoc}
                                style={{ display: 'none' }}
                              />
                            </label>
                            <span style={{ fontSize: '0.85rem', color: '#475569' }}>
                              {isCompressingDoc ? "Comprimiendo archivo..." : selectedDocument ? "1 de 1 archivo cargado" : "0 de 1 archivo cargado"}
                            </span>
                          </div>

                          {docErrors && (
                            <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '8px', fontWeight: 500 }}>
                              {docErrors}
                            </div>
                          )}

                          {selectedDocument && (
                            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '8px 12px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '0.85rem'
                              }}>
                                <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '80%', color: '#334155', fontWeight: 500 }}>
                                  {selectedDocument.file.name} ({(selectedDocument.file.size / (1024 * 1024)).toFixed(2)} MB)
                                </span>
                                <button
                                  type="button"
                                  onClick={removeDocument}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#ef4444',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    padding: '2px 6px',
                                    fontSize: '0.9rem'
                                  }}
                                >
                                  &times;
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                      {/* ¿Contiene baterías? */}
                      <div className={`${styles.mt15}`}>
                        <div
                          className={styles.checkboxRow}
                          style={{ opacity: values.parcelOrdDoc === "DOC" ? 0.6 : 1, cursor: values.parcelOrdDoc === "DOC" ? "not-allowed" : "pointer" }}
                          onClick={() => { if (values.parcelOrdDoc !== "DOC") setValues((prev: any) => ({ ...prev, hasBattery: !prev.hasBattery })); }}
                        >
                          <input type="checkbox" name="hasBattery" checked={values.hasBattery}
                            disabled={values.parcelOrdDoc === "DOC"}
                            onChange={(e) => { e.stopPropagation(); if (values.parcelOrdDoc !== "DOC") setValues((prev: any) => ({ ...prev, hasBattery: e.target.checked })); }} />
                          <span>El paquete contiene baterías (ej. Laptops, teléfonos, etc.)</span>
                        </div>
                      </div>

                      {/* Disclaimer */}
                      <div className={`${styles.mt15}`} id="disclaimer-checkbox-container">
                        <div
                          className={`${styles.checkboxRow} ${disclaimerError ? styles.error : ""}`}
                          onClick={() => { const next = !disclaimerChecked; setDisclaimerChecked(next); if (next) setDisclaimerError(false); }}
                        >
                          <input type="checkbox" checked={disclaimerChecked}
                            onChange={(e) => { e.stopPropagation(); setDisclaimerChecked(e.target.checked); if (e.target.checked) setDisclaimerError(false); }} />
                          <span>
                            Confirmo que las dimensiones y el peso ingresados son verídicos. Entiendo que están sujetos a recálculo y auditoría por parte del courier final.
                          </span>
                        </div>
                        {disclaimerError && (
                          <span style={{ display: "block", marginTop: "6px", paddingLeft: "4px", color: "#ef4444", fontSize: "12px", fontWeight: 500 }}>
                            Debes confirmar las dimensiones y el peso para continuar.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={`${styles.stepActions} ${styles.mt30}`}>
                    <button className={styles.btnNext} onClick={handleNextStep}>
                      Siguiente <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                </div>
              )}

              {/* ================================================================
                  PASO 2 – Selección de Direcciones (igual a SendPackagePageV2 + indicador)
              ================================================================ */}
              {currentStep === 2 && (
                <div className={styles.fadeIn}>
                  <h4 className={styles.stepHeader}>Origen y Destino</h4>

                  <div style={{ color: "#475569", fontSize: "14px", marginBottom: "20px", fontWeight: 500 }}>
                    Seleccione una dirección de origen y una dirección de destino para continuar.
                  </div>

                  {/* Pais origen / destino */}
                  <div className={styles.locationSelects}>
                    <div className={styles.formGroup} style={{ flex: 1 }}>
                      <label>País Origen</label>
                      <select name="origin" value={values.origin} onChange={handleChange} disabled>
                        <option value="US">Estados Unidos</option>
                      </select>
                    </div>
                    <button type="button" className={styles.swapBtn} disabled>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M7 16V4M7 4L3 8M7 4L11 8M17 8V20M17 20L21 16M17 20L13 16" />
                      </svg>
                    </button>
                    <div className={styles.formGroup} style={{ flex: 1, position: "relative" }}>
                      <label>País Destino</label>
                      <select
                        id="destination"
                        name="destination"
                        value={values.destination}
                        className={errors.destination ? styles.isInvalid : ""}
                        onChange={(e) => {
                          handleChange(e);
                          setSelectedDestId(null);
                          setValues((prev: any) => ({
                            ...prev,
                            destinationZip: "", destinationNickname: "", destinationAddressLine1: "",
                            destinationAddressLine2: "", destinationCity: "", destinationState: "",
                            receiverFirstName: "", receiverLastName: "", receiverEmail: "",
                            receiverPhone: "", receiverKindOfDNI: "", receiverDNI: "",
                          }));
                        }}
                      >
                        <option value="">Seleccionar País</option>
                        {receiverCountries.map((c) => (
                          <option key={c.id} value={c.iso2Country}>{c.name}</option>
                        ))}
                      </select>
                      {errors.destination && (
                        <span className={styles.invalidFeedback} style={{ position: "absolute", top: "100%", left: 0 }}>
                          {errors.destination}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Address columns */}
                  <div className={styles.addressColumns}>
                    {/* Origen */}
                    <div className={styles.addressSection}>
                      <h5 className={styles.addressSectionTitle}>
                        Dirección de Origen
                      </h5>
                      <p style={{ fontSize: "12px", color: "#64748b", margin: "-6px 0 12px 0" }}>
                        (Máximo 5 direcciones registradas)
                      </p>
                      <div className={styles.addressCardsGrid}>
                        {!isOriginLimitReached && (
                          <div
                            className={styles.addNewCard}
                            onClick={() => { setAddressModalType("origin"); setShowAddressModal(true); }}
                          >
                            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--brand-yellow)", color: "var(--brand-black)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                              <FontAwesomeIcon icon={faPlus} />
                            </div>
                            <div>
                              <strong style={{ display: "block", fontSize: 13, fontWeight: 700, color: "var(--brand-black)" }}>Agregar Nueva</strong>
                              <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>Ingresar datos manualmente</p>
                            </div>
                          </div>
                        )}
                        {usaAddresses.map((addr, idx) => (
                          <AddressSelectionCard
                            key={addr.id || idx}
                            address={addr}
                            isSelected={selectedOriginId === addr.id}
                            icon={faMapMarkerAlt}
                            onDelete={() => addr.id && handleDeleteAddress(addr.id)}
                            onEdit={() => handleEditAddressClick(addr)}
                            onClick={() => {
                              setSelectedOriginId(addr.id || null);
                              setValues((prev: any) => ({
                                ...prev,
                                originZip: addr.zipCode,
                                originNickname: addr.nickname,
                                originAddressLine1: addr.addressLine1,
                                originAddressLine2: addr.addressLine2 || "",
                                originCity: addr.city,
                                originState: addr.state,
                                originFirstName: addr.addressSubjet?.firstName || "",
                                originLastName: addr.addressSubjet?.lastName || "",
                                originPhone: addr.addressSubjet?.phoneNumber || "",
                                originEmail: addr.addressSubjet?.email || "",
                                originKindOfDNI: addr.addressSubjet?.kindOfDNI || "",
                                originDNI: addr.addressSubjet?.dni || "",
                              }));
                              validateZip("origin", addr.zipCode, "US");
                            }}
                          />
                        ))}
                        {usaAddresses.length === 0 && (
                          <div className={styles.addressCardEmpty}>No tienes direcciones de origen registradas.</div>
                        )}
                      </div>
                    </div>

                    {/* Destino */}
                    <div className={styles.addressSection}>
                      <h5 className={styles.addressSectionTitle}>
                        Dirección de Destino
                      </h5>
                      <p style={{ fontSize: "12px", color: "#64748b", margin: "-6px 0 12px 0" }}>
                        (Máximo 5 direcciones registradas)
                      </p>
                      {!values.destination ? (
                        <div className={styles.addressCardEmpty}>Selecciona un país de destino para ver las direcciones disponibles.</div>
                      ) : (
                        <div className={styles.addressCardsGrid}>
                          {!isDestinationLimitReached && (
                            <div
                              className={styles.addNewCard}
                              onClick={() => { setAddressModalType("destination"); setShowAddressModal(true); }}
                            >
                              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--brand-yellow)", color: "var(--brand-black)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                                <FontAwesomeIcon icon={faPlus} />
                              </div>
                              <div>
                                <strong style={{ display: "block", fontSize: 13, fontWeight: 700, color: "var(--brand-black)" }}>Agregar Nueva</strong>
                                <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>Ingresar datos manualmente</p>
                              </div>
                            </div>
                          )}
                          {destinationAddresses.map((addr, idx) => (
                            <AddressSelectionCard
                              key={addr.id || idx}
                              address={addr}
                              isSelected={selectedDestId === addr.id}
                              icon={faMapMarkerAlt}
                              onDelete={() => addr.id && handleDeleteAddress(addr.id)}
                              onEdit={() => handleEditAddressClick(addr)}
                              onClick={async () => {
                                setSelectedDestId(addr.id || null);
                                const baseValues = {
                                  ...values,
                                  destinationZip: addr.zipCode,
                                  destinationNickname: addr.nickname,
                                  destinationAddressLine1: addr.addressLine1,
                                  destinationAddressLine2: addr.addressLine2 || "",
                                  destinationCity: addr.city,
                                  destinationState: addr.state,
                                  receiverFirstName: addr.addressSubjet?.firstName || "",
                                  receiverLastName: addr.addressSubjet?.lastName || "",
                                  receiverEmail: addr.addressSubjet?.email || "",
                                  receiverPhone: addr.addressSubjet?.phoneNumber || "",
                                  receiverKindOfDNI: addr.addressSubjet?.kindOfDNI || "",
                                  receiverDNI: addr.addressSubjet?.dni || "",
                                  municipalityDestiny: addr.city,
                                  stateDestiny: addr.state,
                                };
                                setValues(baseValues);
                                const googleLocation = await fetchMunicipality(addr.city, addr.state, addr.zipCode, addr.isO2Country || values.destination);
                                if (googleLocation) {
                                  setValues((v: any) => ({ ...v, municipalityDestiny: googleLocation.municipality, stateDestiny: googleLocation.state }));
                                }
                                validateZip("destination", addr.zipCode, addr.isO2Country || values.destination);
                              }}
                            />
                          ))}
                          {destinationAddresses.length === 0 && (
                            <div className={styles.addressCardEmpty}>No tienes direcciones de destino para este país.</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>


                  <div className={`${styles.stepActions} ${styles.mt30}`}>
                    <button className={styles.btnBack} onClick={handleBackStep}>
                      <FontAwesomeIcon icon={faChevronLeft} /> Atrás
                    </button>
                    <button
                      className={styles.btnNext}
                      onClick={handleNextStep}
                      disabled={!isStep2Valid || isCalculating}
                    >
                      {isCalculating
                        ? <><FontAwesomeIcon icon={faSpinner} spin /> Calculando...</>
                        : <>Siguiente <FontAwesomeIcon icon={faChevronRight} /></>}
                    </button>
                  </div>
                </div>
              )}

              {/* ================================================================
                  PASO 3 – Selección de Servicio (igual a SendPackagePageV2)
              ================================================================ */}
              {currentStep === 3 && (
                <div className={styles.fadeIn}>
                  <div className={styles.servicesWrapper}>
                    {isCalculating && (
                      <div className={styles.calculatingOverlay}>
                        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <div className={styles.spinner} />
                          <img src={parcelIMG} alt="Cargando..." style={{ position: "absolute", width: 28, height: 28, objectFit: "contain" }} />
                        </div>
                        <p style={{ color: "#475569", fontWeight: 600 }}>Calculando tarifas...</p>
                      </div>
                    )}

                    {rateResults.length > 0 ? (
                      <div className={styles.carrierSelectionContainer}>
                        {values.origin === values.destination ? (
                          /* Modo Nacional */
                          <>
                            <div className={styles.mainCarriersCol}>
                              <h4>
                                Couriers disponibles para
                                <span style={{ display: 'block', fontSize: '1.3em', fontWeight: 800, marginTop: '2px', color: 'var(--brand-black)' }}>
                                  {getCountryName(values.destination)}
                                </span>
                              </h4>
                              <div className={styles.servicesGrid}>
                                <ServiceCard
                                  rateOption={rateResults[0]}
                                  isSelected={true}
                                  cleanCard={true}
                                  estimatedTotal={0}
                                  selectedExternalIndices={new Set()}
                                  onSelectExternalService={() => {}}
                                  onViewDetails={() => {}}
                                  onClick={() => {}}
                                />
                              </div>
                            </div>

                            <div className={styles.externalServicesCol}>
                              {(externalServices[0]?.elpaketDispatcherService || externalServices[0]?.pickUpService || externalServices.length > 0) && (
                              <div className={styles.pickupCardSection} style={{ marginBottom: '20px' }}>
                                <h5 style={{ fontSize: '1.15rem', fontWeight: 700, margin: '0 0 8px', color: 'var(--brand-black)' }}>Drop Off / Pickup</h5>
                                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                                  Selecciona cómo nos harás llegar tu paquete.
                                </p>

                                {(externalServices[0]?.elpaketDispatcherService || externalServices[0]?.pickUpService || externalServices.length > 0) && (
                                  <div className={styles.pickupGrid}>
                                     <div
                                       className={`${additionalStyles.cardContainer} ${(!pickupSelected && !elpaketDispatcherSelected) ? additionalStyles.selected : ""}`}
                                       onClick={() => {
                                         setPickupSelected(false);
                                         setElpaketDispatcherSelected(false);
                                         userWantsPickupRef.current = false;
                                         userWantsElpaketDispatcherRef.current = false;
                                       }}
                                       style={{ cursor: 'pointer' }}
                                     >
                                       <div className={additionalStyles.cardMainLayout}>
                                         <div className={additionalStyles.textContent}>
                                           <div className={additionalStyles.topRow}>
                                             <div className={additionalStyles.contentRow}>
                                               <div className={additionalStyles.checkWrapper}>
                                                 {(!pickupSelected && !elpaketDispatcherSelected) ? (
                                                   <FontAwesomeIcon icon={faCheckSquare} className={additionalStyles.checkIconActive} />
                                                 ) : (
                                                   <FontAwesomeIcon icon={faSquare} className={additionalStyles.checkIconInactive} />
                                                 )}
                                               </div>
                                               <span className={additionalStyles.serviceName}>DROP OFF</span>
                                             </div>
                                             <div className={additionalStyles.freeBadge}>
                                               INCLUIDO
                                             </div>
                                           </div>
                                           <div className={additionalStyles.priceContainer}>
                                             <div className={additionalStyles.priceRow}>
                                               <span className={additionalStyles.priceValue}>$0.00</span>
                                               <span className={additionalStyles.currency}>USD</span>
                                             </div>
                                             <span className={additionalStyles.subtitleText}>
                                               Entregar el paquete en la tienda UPS de su preferencia.
                                             </span>
                                           </div>
                                         </div>
                                         <div className={additionalStyles.illustrationWrapper}>
                                           <img src={dropoffStoreImg} alt="Tienda UPS Drop Off" className={additionalStyles.cardIllustration} />
                                         </div>
                                       </div>
                                     </div>

                                    {externalServices[0]?.elpaketDispatcherService ? (
                                      <AdditionalExternalServiceCard
                                        pickupDate={values.pickupDate}
                                        onDateChange={handleChange}
                                        minDate={getTomorrowDate()}
                                        maxDate={getMaxDate()}
                                        service={{
                                          serviceName: externalServices[0].elpaketDispatcherService.serviceName,
                                          serviceQty: externalServices[0].elpaketDispatcherService.serviceQty,
                                          servicePrice: externalServices[0].elpaketDispatcherService.servicePrice,
                                          serviceSubTotal: externalServices[0].elpaketDispatcherService.serviceSubTotal,
                                          idExternalCourierService: externalServices[0].elpaketDispatcherService.idExternalCourierService,
                                          transitDays: externalServices[0].elpaketDispatcherService.transitDays || "N/A",
                                          standardPrice: externalServices[0].elpaketDispatcherService.standardPrice,
                                          discountPercent: externalServices[0].elpaketDispatcherService.discountPercent,
                                        }}
                                        isSelected={elpaketDispatcherSelected}
                                        onClick={() => {
                                          setElpaketDispatcherSelected(true);
                                          setPickupSelected(false);
                                          userWantsPickupRef.current = false;
                                          userWantsElpaketDispatcherRef.current = true;
                                        }}
                                        currency={currentSelectedRate?.currency || "USD"}
                                      />
                                    ) : externalServices[0]?.pickUpService ? (
                                      <AdditionalExternalServiceCard
                                        pickupDate={values.pickupDate}
                                        onDateChange={handleChange}
                                        minDate={getTomorrowDate()}
                                        maxDate={getMaxDate()}
                                        service={{
                                          serviceName: externalServices[0].pickUpService.serviceName,
                                          serviceQty: externalServices[0].pickUpService.serviceQty,
                                          servicePrice: externalServices[0].pickUpService.servicePrice,
                                          serviceSubTotal: externalServices[0].pickUpService.serviceSubTotal,
                                          idExternalCourierService: externalServices[0].pickUpService.idExternalCourierService,
                                          transitDays: externalServices[0].pickUpService.transitDays || "N/A",
                                          standardPrice: externalServices[0].pickUpService.standardPrice,
                                          discountPercent: externalServices[0].pickUpService.discountPercent,
                                        }}
                                        isSelected={pickupSelected}
                                        onClick={() => {
                                          setPickupSelected(true);
                                          setElpaketDispatcherSelected(false);
                                          userWantsPickupRef.current = true;
                                          userWantsElpaketDispatcherRef.current = false;
                                        }}
                                        currency={currentSelectedRate?.currency || "USD"}
                                      />
                                    ) : null}
                                  </div>
                                )}
                              </div>
                              )}

                              {!elpaketDispatcherSelected && (
                                <div className={styles.externalServicesWrapper}>
                                  <h5>Traslado Nacional - Servicios disponibles ({rateResults[0]?.carrierName || "UPS"})</h5>
                                  <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                                    Selecciona obligatoriamente un servicio de envío para tu etiqueta.
                                  </p>
                                  {rateResults[0]?.services && rateResults[0].services.length > 0 && (
                                    <div className={styles.externalCardsGrid} style={{ marginBottom: '24px' }}>
                                      {(() => {
                                        const services = [...rateResults[0].services].sort((a, b) => a.serviceSubTotal - b.serviceSubTotal);
                                        const minPrice = services[0]?.serviceSubTotal ?? 0;
                                        return services.map((option, optIdx) => {
                                          let badgeType: "lowest" | "recommended" | "express" | null = null;
                                          if (option.transitDays === "1") {
                                            badgeType = "express";
                                          } else if (option.serviceSubTotal === minPrice && services.length > 1) {
                                            badgeType = "lowest";
                                          } else if (
                                            option.serviceName.toLowerCase().includes("2nd day") ||
                                            option.serviceName.toLowerCase().includes("air")
                                          ) {
                                            badgeType = "recommended";
                                          }
                                          const isSelected =
                                            values.idCarrierService === option.idCarrierService ||
                                            (!values.idCarrierService && optIdx === 0);
                                          return (
                                            <ExternalServiceCard
                                              key={optIdx}
                                              service={{
                                                serviceName: option.serviceName,
                                                serviceQty: option.serviceQty,
                                                servicePrice: option.servicePrice,
                                                serviceSubTotal: option.serviceSubTotal,
                                                idExternalCourierService: option.idCarrierService || optIdx,
                                                transitDays: option.transitDays || "4",
                                                estimatedDeliveryDate: option.estimatedDeliveryDate,
                                                standardPrice: option.standardPrice,
                                                discountPercent: option.discountPercent,
                                              }}
                                              isSelected={isSelected}
                                              onClick={() => {
                                                const updated = {
                                                  ...values,
                                                  selectedService: rateResults[0].carrierName,
                                                  idCarrier: rateResults[0].idCarrier || 1,
                                                  idCarrierService: option.idCarrierService ?? undefined,
                                                };
                                                setValues(updated);
                                                fetchExternalRatesHelper(updated);
                                              }}
                                              currency={rateResults[0].currency || "USD"}
                                              badgeType={badgeType}
                                            />
                                          );
                                        });
                                      })()}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          /* Modo Internacional */
                          <>
                            <div className={styles.mainCarriersCol}>
                              <h4>
                                Couriers disponibles para
                                <span style={{ display: 'block', fontSize: '1.3em', fontWeight: 800, marginTop: '2px', color: 'var(--brand-black)' }}>
                                  {getCountryName(values.destination)}
                                </span>
                              </h4>
                              {rateResults.length > 1 && (
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  background: 'rgba(255, 193, 20, 0.08)',
                                  border: '1.5px dashed var(--elPaket-primary-color, #ffc114)',
                                  borderRadius: '10px',
                                  padding: '8px 14px',
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  color: 'var(--brand-black)',
                                  margin: '4px 0 16px'
                                }}>
                                  <FontAwesomeIcon icon={faHandPointer} style={{ color: 'var(--elPaket-primary-color, #ffc114)' }} />
                                  Selecciona el Courier de tu preferencia
                                </div>
                              )}
                              <div className={styles.servicesGrid}>
                                {rateResults.map((rate, idx) => {
                                  const estimatedTotal = rateService.calculateTotal(
                                    rate,
                                    selectedExternalIndicesByCarrier.get(getServiceKey(rate)) || rateService.getInitialExternalIndices(rate),
                                  );
                                  const isSelected = values.selectedService === rate.carrierName && values.idCarrierService === rate.idCarrierService;
                                  return (
                                    <ServiceCard
                                      key={idx}
                                      rateOption={rate}
                                      estimatedTotal={estimatedTotal}
                                      isSelected={isSelected}
                                      onClick={() => {
                                        const updated = {
                                          ...values,
                                          selectedService: rate.carrierName,
                                          idCarrier: rate.idCarrier || 1,
                                          idCarrierService: rate.idCarrierService ?? undefined,
                                        };
                                        setValues(updated);
                                        fetchExternalRatesHelper(updated);
                                      }}
                                      selectedExternalIndices={selectedExternalIndicesByCarrier.get(getServiceKey(rate)) || new Set()}
                                      onSelectExternalService={(index) => selectExternalService(rate, index)}
                                      isCurrentSelectedService={false}
                                      onViewDetails={() => { setSelectedRateForModal(rate); setShowServiceDetailModal(true); }}
                                      estimatedArrivalText={getEstimatedArrivalText(rate)}
                                    />
                                  );
                                })}
                              </div>
                            </div>

                            {values.selectedService && (
                              <div className={styles.externalServicesCol}>
                                {!loadingExternal && externalServices.length > 0 && (externalServices[0]?.elpaketDispatcherService || externalServices[0]?.pickUpService) && (
                                  <div className={styles.pickupCardSection} style={{ marginBottom: '20px' }}>
                                    <h5 style={{ fontSize: '1.15rem', fontWeight: 700, margin: '0 0 8px', color: 'var(--brand-black)' }}>Drop Off / Pickup</h5>
                                    <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                                      Selecciona cómo nos harás llegar tu paquete.
                                    </p>
                                      {(externalServices[0]?.elpaketDispatcherService || externalServices[0]?.pickUpService) && (
                                        <div className={styles.pickupGrid}>
                                          {(externalServices[0]?.providerName?.toLowerCase().includes("ups") || false) && (
                                            <div
                                              className={`${additionalStyles.cardContainer} ${(!pickupSelected && !elpaketDispatcherSelected) ? additionalStyles.selected : ''}`}
                                              onClick={() => {
                                                setPickupSelected(false);
                                                setElpaketDispatcherSelected(false);
                                              }}
                                              style={{ cursor: 'pointer' }}
                                            >
                                              <div className={additionalStyles.cardMainLayout}>
                                                <div className={additionalStyles.textContent}>
                                                  <div className={additionalStyles.topRow}>
                                                    <div className={additionalStyles.contentRow}>
                                                      <div className={additionalStyles.checkWrapper}>
                                                        {(!pickupSelected && !elpaketDispatcherSelected) ? (
                                                          <FontAwesomeIcon icon={faCheckSquare} className={additionalStyles.checkIconActive} />
                                                        ) : (
                                                          <FontAwesomeIcon icon={faSquare} className={additionalStyles.checkIconInactive} />
                                                        )}
                                                      </div>
                                                      <span className={additionalStyles.serviceName}>DROP OFF</span>
                                                    </div>
                                                    <div className={additionalStyles.freeBadge}>
                                                      INCLUIDO
                                                    </div>
                                                  </div>

                                                  <div className={additionalStyles.priceContainer}>
                                                    <div className={additionalStyles.priceRow}>
                                                      <span className={additionalStyles.priceValue}>$0.00</span>
                                                      <span className={additionalStyles.currency}>USD</span>
                                                    </div>
                                                    <span className={additionalStyles.subtitleText}>
                                                      Entregar el paquete en la tienda UPS de su preferencia.
                                                    </span>
                                                  </div>
                                                </div>

                                                <div className={additionalStyles.illustrationWrapper}>
                                                  <img src={dropoffStoreImg} alt="Tienda UPS Drop Off" className={additionalStyles.cardIllustration} />
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          {externalServices[0]?.elpaketDispatcherService ? (
                                            <AdditionalExternalServiceCard
                                              pickupDate={values.pickupDate}
                                              onDateChange={handleChange}
                                              minDate={getTomorrowDate()}
                                              maxDate={getMaxDate()}
                                              service={{
                                                serviceName: externalServices[0].elpaketDispatcherService.serviceName,
                                                serviceQty: externalServices[0].elpaketDispatcherService.serviceQty,
                                                servicePrice: externalServices[0].elpaketDispatcherService.servicePrice,
                                                serviceSubTotal: externalServices[0].elpaketDispatcherService.serviceSubTotal,
                                                idExternalCourierService: externalServices[0].elpaketDispatcherService.idExternalCourierService,
                                                transitDays: externalServices[0].elpaketDispatcherService.transitDays || "N/A",
                                                standardPrice: externalServices[0].elpaketDispatcherService.standardPrice,
                                                discountPercent: externalServices[0].elpaketDispatcherService.discountPercent,
                                              }}
                                              isSelected={elpaketDispatcherSelected}
                                              onClick={() => {
                                                setElpaketDispatcherSelected(true);
                                                setPickupSelected(false);
                                                userWantsPickupRef.current = false;
                                                userWantsElpaketDispatcherRef.current = true;
                                              }}
                                              currency={currentSelectedRate?.currency || "USD"}
                                            />
                                          ) : (
                                            <AdditionalExternalServiceCard
                                              pickupDate={values.pickupDate}
                                              onDateChange={handleChange}
                                              minDate={getTomorrowDate()}
                                              maxDate={getMaxDate()}
                                              service={{
                                                serviceName: externalServices[0].pickUpService!.serviceName,
                                                serviceQty: externalServices[0].pickUpService!.serviceQty,
                                                servicePrice: externalServices[0].pickUpService!.servicePrice,
                                                serviceSubTotal: externalServices[0].pickUpService!.serviceSubTotal,
                                                idExternalCourierService: externalServices[0].pickUpService!.idExternalCourierService,
                                                transitDays: externalServices[0].pickUpService!.transitDays || "N/A",
                                                standardPrice: externalServices[0].pickUpService!.standardPrice,
                                                discountPercent: externalServices[0].pickUpService!.discountPercent,
                                              }}
                                              isSelected={pickupSelected}
                                              onClick={() => {
                                                setPickupSelected(true);
                                                setElpaketDispatcherSelected(false);
                                                userWantsPickupRef.current = true;
                                                userWantsElpaketDispatcherRef.current = false;
                                              }}
                                              currency={currentSelectedRate?.currency || "USD"}
                                            />
                                          )}
                                        </div>
                                      )}
                                  </div>
                                )}

                                {!elpaketDispatcherSelected && (
                                  <div className={styles.externalServicesWrapper}>
                                    <h5>Traslado Nacional - Servicios disponibles ({externalServices[0]?.providerName || "UPS"})</h5>
                                    {loadingExternal ? (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '20px 0' }}>
                                        <FontAwesomeIcon icon={faSpinner} spin style={{ color: 'var(--brand-yellow)' }} />
                                        <span>Cargando servicios externos...</span>
                                      </div>
                                    ) : externalServices.length > 0 ? (
                                      <>
                                        <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                                          Selecciona obligatoriamente un servicio de envío para tu etiqueta.
                                        </p>
                                        <div className={styles.externalCardsGrid} style={elpaketDispatcherSelected ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                                          {externalServices.flatMap(provider => {
                                            const sortedOpts = [...(provider.options || [])].sort((a, b) => a.serviceSubTotal - b.serviceSubTotal);
                                            const allOptions = externalServices.flatMap(p => p.options || []);
                                            const minPrice = allOptions.length > 0 ? Math.min(...allOptions.map(o => o.serviceSubTotal)) : 0;
                                            return sortedOpts.map((option, optIdx) => {
                                              let badgeType: 'lowest' | 'recommended' | 'express' | null = null;
                                              if (option.transitDays === "1") {
                                                badgeType = 'express';
                                              } else if (option.serviceSubTotal === minPrice && allOptions.length > 1) {
                                                badgeType = 'lowest';
                                              } else if (option.serviceName.toLowerCase().includes('2nd day') || option.serviceName.toLowerCase().includes('air')) {
                                                badgeType = 'recommended';
                                              }
                                              return (
                                                <ExternalServiceCard
                                                  key={optIdx}
                                                  service={{
                                                    serviceName: option.serviceName,
                                                    serviceQty: option.serviceQty,
                                                    servicePrice: option.servicePrice,
                                                    serviceSubTotal: option.serviceSubTotal,
                                                    idExternalCourierService: option.idExternalCourierService,
                                                    transitDays: option.transitDays || 'N/A',
                                                    estimatedDeliveryDate: option.estimatedDeliveryDate,
                                                    standardPrice: option.standardPrice,
                                                    discountPercent: option.discountPercent,
                                                  }}
                                                  isSelected={!elpaketDispatcherSelected && selectedExternalOption?.idExternalCourierService === option.idExternalCourierService}
                                                  onClick={() => {
                                                    setSelectedExternalOption(option);
                                                    setElpaketDispatcherSelected(false);
                                                  }}
                                                  currency={currentSelectedRate?.currency || 'USD'}
                                                  badgeType={badgeType}
                                                />
                                              );
                                            });
                                          })}
                                        </div>
                                      </>
                                    ) : (
                                      <p style={{ fontSize: '13px', color: '#64748b' }}>No hay servicios externos disponibles para este courier.</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ) : !isCalculating ? (
                      <div className={styles.noResultsBox}>
                        <div className={styles.noResultsIcon}>
                          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                          </svg>
                        </div>
                        <div>
                          <p style={{ margin: "0 0 6px", fontSize: "1rem", fontWeight: 700, color: "#1e293b" }}>No encontramos carriers disponibles</p>
                          <p style={{ margin: 0, fontSize: "0.875rem", color: "#64748b", lineHeight: 1.6 }}>
                            No hay opciones de envío para los parámetros seleccionados.<br />
                            Intenta ajustar el destino, las dimensiones o el método de envío.
                          </p>
                        </div>
                        <button type="button" className={styles.btnBack} onClick={handleBackStep}>
                          <FontAwesomeIcon icon={faChevronLeft} /> Volver y ajustar datos
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {rateResults.length > 0 && values.selectedService && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '20px 24px',
                      background: 'var(--elPaket-primary-color-light, #eff6ff)',
                      borderRadius: '12px',
                      border: '1.5px solid var(--elPaket-primary-color)',
                      marginTop: '20px',
                      marginBottom: '20px'
                    }}>
                      <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--brand-black)' }}>Total estimado</span>
                      <span style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--brand-black)' }}>
                        {dynamicTotal.toFixed(2)} {currentSelectedRate?.currency || 'USD'}
                      </span>
                    </div>
                  )}

                  {rateResults.length > 0 && (
                    <div className={`${styles.stepActions} ${styles.mt30}`}>
                      <button className={styles.btnBack} onClick={handleBackStep}>
                        <FontAwesomeIcon icon={faChevronLeft} /> Atrás
                      </button>
                      <button
                        className={styles.btnNext}
                        onClick={handleNextStep}
                        disabled={!values.selectedService || (values.origin !== values.destination && !selectedExternalOption && !elpaketDispatcherSelected) || loadingExternal}
                      >
                        Siguiente <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ================================================================
                  PASO 4 – Resumen (como paso 5 de PickUpPage)
              ================================================================ */}
              {currentStep === 4 && currentSelectedRate && (
                <div className={styles.fadeIn}>
                  <div className={styles.summaryLayout}>
                    {/* Left: cards */}
                    <div className={styles.summaryLeftColumn}>
                      {/* Paquete */}
                      <div className={styles.summaryCard}>
                        <h5 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span><FontAwesomeIcon icon={faBox} /> Resumen de paquete</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div
                              className={styles.editIcon}
                              onClick={() => setShowPackageDetailsModal(true)}
                              title="Ver detalles del paquete"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </div>
                            <div
                              className={styles.editIcon}
                              onClick={() => setShowEditPackageModal(true)}
                              title="Editar detalles del paquete"
                            >
                              <FontAwesomeIcon icon={faPencilAlt} />
                            </div>
                          </div>
                        </h5>
                        <div className={styles.summaryDetails}>
                          {packages && packages.length > 1 ? (
                            <>
                              <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Peso Total:</span>
                                <span className={styles.detailValue}>
                                  {values.weight || packages.reduce((acc: number, p: any) => acc + (Number(p.weight) || 0), 0)}{" "}
                                  {values.unitSystem === "metric" ? "kg" : "lb"}
                                </span>
                              </div>
                              <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Valor FOB Total:</span>
                                <span className={styles.detailValue}>
                                  ${values.fob || packages.reduce((acc: number, p: any) => acc + (Number(p.fob) || 0), 0)} {values.currency || "USD"}
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Tipo:</span>
                                <span className={styles.detailValue}>
                                  {values.parcelOrdDoc === "DOC" ? "Documento" : "Caja"}
                                </span>
                              </div>
                              <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Peso:</span>
                                <span className={styles.detailValue}>
                                  {values.weight || packages[0]?.weight}{" "}
                                  {values.unitSystem === "metric" ? "kg" : "lb"}
                                </span>
                              </div>
                              <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Dimensiones:</span>
                                <span className={styles.detailValue}>
                                  {(values.length || packages[0]?.length) || "0"}x{(values.width || packages[0]?.width) || "0"}x{(values.height || packages[0]?.height) || "0"}{" "}
                                  {values.unitSystem === "metric" ? "cm" : "in"}
                                </span>
                              </div>
                              <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Valor FOB:</span>
                                <span className={styles.detailValue}>
                                  ${values.fob || packages[0]?.fob} {values.currency || "USD"}
                                </span>
                              </div>
                              <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Contenido:</span>
                                <span className={styles.detailValue}>
                                  {values.packageContent || packages[0]?.packageContent}
                                </span>
                              </div>
                              {values.parcelOrdDoc === "PARCEL" && (
                                <div className={styles.detailItem}>
                                  <span className={styles.detailLabel}>Frágil:</span>
                                  <span className={styles.detailValue}>
                                    {packages.some(p => p.isFragile) ? "Sí" : "No"}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Remitente (origen) */}
                      <div className={styles.summaryCard}>
                        <h5 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span><FontAwesomeIcon icon={faMapMarkerAlt} /> Remitente <small style={{ fontSize: '0.75em', fontWeight: 'normal', color: '#64748b', marginLeft: '4px' }}>(Persona entrega el paquete)</small></span>
                          <div
                            className={styles.editIcon}
                            onClick={() => setShowEditOriginModal(true)}
                            title="Editar remitente"
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </div>
                        </h5>
                        <div className={styles.summaryDetails}>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Nombre:</span>
                            <span className={styles.detailValue}>
                              {values.originFirstName || tokenService.getCustomerDataToken()?.customerData.FirstName}{" "}
                              {values.originLastName || tokenService.getCustomerDataToken()?.customerData.LastName}
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Dirección:</span>
                            <span className={styles.detailValue}>
                              {[
                                values.originAddressLine1,
                                values.originCity,
                                values.originState,
                                values.originZip,
                                getCountryName(values.origin),
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Teléfono:</span>
                            <span className={styles.detailValue}>
                              {addresses.find((a) => a.id === selectedOriginId)?.addressSubjet?.phoneNumber || values.originPhone || tokenService.getCustomerDataToken()?.customerData.PhoneNumber}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Destinatario */}
                      <div className={styles.summaryCard}>
                        <h5 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span><FontAwesomeIcon icon={faTruck} /> Destinatario <small style={{ fontSize: '0.75em', fontWeight: 'normal', color: '#64748b', marginLeft: '4px' }}>(Persona recibe el paquete)</small></span>
                          <div
                            className={styles.editIcon}
                            onClick={() => setShowEditDestModal(true)}
                            title="Editar destinatario"
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </div>
                        </h5>
                        <div className={styles.summaryDetails}>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Nombre:</span>
                            <span className={styles.detailValue}>
                              {values.receiverFirstName}{" "}
                              {values.receiverLastName}
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Dirección:</span>
                            <span className={styles.detailValue}>
                              {[
                                values.destinationAddressLine1,
                                values.destinationCity,
                                values.destinationState,
                                values.destinationZip,
                                getCountryName(values.destination),
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Teléfono:</span>
                            <span className={styles.detailValue}>
                              {values.receiverPhone}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right: cost + actions */}
                    <div className={styles.summaryRightColumn}>
                      <div className={styles.costCard}>
                        <h5><FontAwesomeIcon icon={faCreditCard} /> Resumen de Costos</h5>
                        <div className={styles.costBreakdown}>
                          {(() => {
                            const isNat = values.origin === values.destination;
                            const nationalServices = isNat ? (rateResults[0]?.services || []) : [];
                            const selectedSubSvc = isNat
                              ? (nationalServices.find(s => s.idCarrierService === values.idCarrierService) || nationalServices[0])
                              : null;
                            const carrierAmount = isNat
                              ? (selectedSubSvc?.serviceSubTotal ?? 0)
                              : currentSelectedRate.services.reduce((acc, s) => acc + s.serviceSubTotal, 0);
                            const carrierLabel = isNat
                              ? (selectedSubSvc?.serviceName ?? currentSelectedRate.carrierName)
                              : (currentSelectedRate.serviceIndicator
                                ? `${currentSelectedRate.carrierName} - ${currentSelectedRate.serviceIndicator}`
                                : currentSelectedRate.carrierName);
                            return (
                              <div className={styles.costItem}>
                                <span>{carrierLabel}</span>
                                <span>{carrierAmount.toFixed(2)} {currentSelectedRate.currency}</span>
                              </div>
                            );
                          })()}

                        {!(values.origin === values.destination) && selectedExternalOption && !elpaketDispatcherSelected && (
                          <div className={styles.costItem}>
                            <span>{selectedExternalOption.serviceName}</span>
                            <span>{selectedExternalOption.serviceSubTotal.toFixed(2)} {currentSelectedRate.currency}</span>
                          </div>
                        )}
                        {elpaketDispatcherSelected && externalServices[0]?.elpaketDispatcherService && (
                          <>
                            <div className={styles.costItem}>
                              <span>{externalServices[0].elpaketDispatcherService.serviceName}</span>
                              <span>{externalServices[0].elpaketDispatcherService.serviceSubTotal.toFixed(2)} {currentSelectedRate.currency}</span>
                            </div>
                            <div className={styles.pickupNotice}>
                              <div>Fecha de recogida: <strong>{values.pickupDate}</strong></div>
                              <div>Horario de recogida: entre las <strong>8:00</strong> y las <strong>17:00</strong></div>
                            </div>
                          </>
                        )}
                        {!elpaketDispatcherSelected && pickupSelected && externalServices[0]?.pickUpService && (
                          <>
                            <div className={styles.costItem}>
                              <span>{externalServices[0].pickUpService.serviceName}</span>
                              <span>{externalServices[0].pickUpService.serviceSubTotal.toFixed(2)} {currentSelectedRate.currency}</span>
                            </div>
                            <div className={styles.pickupNotice}>
                              <div>Fecha de recogida: <strong>{values.pickupDate}</strong></div>
                              <div>Horario de recogida: entre las <strong>8:00</strong> y las <strong>17:00</strong></div>
                            </div>
                          </>
                        )}
                        {!elpaketDispatcherSelected && !pickupSelected && (externalServices[0]?.pickUpService || externalServices[0]?.elpaketDispatcherService) && (
                          <div className={styles.costItem}>
                            <span>DROP OFF</span>
                            <span>0.00 {currentSelectedRate.currency}</span>
                          </div>
                        )}
                           <div className={`${styles.costItem} ${styles.costTotal}`}>
                            <span>TOTAL A PAGAR</span>
                            <span>{dynamicTotal.toFixed(2)} {currentSelectedRate.currency}</span>
                          </div>
                        </div>

                      </div>

                      <div className={styles.summaryActions}>
                        <button className={styles.btnNext} onClick={handleNextStep}>
                          Siguiente <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                        <button className={styles.btnBack} onClick={handleBackStep}>
                          <FontAwesomeIcon icon={faChevronLeft} /> Atrás
                        </button>
                      </div>

                      <div className={styles.shippingIllustrationContainer}>
                        <img src={cajaTransparente} alt="Caja Elpaket" className={styles.shippingIllustration} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ================================================================
                  PASO 5 – Pago (igual a SendPackagePageV2)
              ================================================================ */}
              {currentStep === 5 && currentSelectedRate && (
                <div className={styles.fadeIn}>
                  <div className={styles.paymentLayout}>
                    {/* Left: Stripe form */}
                    <div className={styles.paymentLeftCol}>
                      <div className={styles.paymentCard}>
                        {loadingPayment ? (
                          <div className={styles.paymentLoading}>
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                            <p style={{ marginTop: 15, fontWeight: "bold" }}>Cargando plataforma de pago...</p>
                          </div>
                        ) : stripeError ? (
                          <div style={{ padding: 20, textAlign: "center", color: "#721c24", backgroundColor: "#f8d7da", border: "1px solid #f5c6cb", borderRadius: 8 }}>
                            <FontAwesomeIcon icon={faShieldAlt} size="2x" style={{ marginBottom: 10 }} />
                            <p style={{ fontWeight: "bold" }}>No se pudo cargar la plataforma de pago.</p>
                            <p>Por favor, comprueba tu conexión a internet o desactiva bloqueadores de anuncios y recarga la página.</p>
                            <button onClick={() => window.location.reload()} className={styles.btnNext} style={{ marginTop: 15, padding: "8px 16px", fontSize: 14 }}>
                              Reintentar
                            </button>
                          </div>
                        ) : clientSecret ? (
                          <Elements stripe={stripePromise} options={{ clientSecret, paymentMethodCreation: "manual" }}>
                            <StripePaymentForm
                              amount={dynamicTotal}
                              currency={currentSelectedRate?.currency || "USD"}
                              onSuccess={async (methodId) => {
                                const result = await Swal.fire({
                                  title: "¿Confirmar pago?",
                                  text: `¿Estás seguro de que deseas procesar el pago de ${dynamicTotal.toFixed(2)} ${currentSelectedRate?.currency || "USD"} y crear el envío?`,
                                  icon: "question",
                                  showCancelButton: true,
                                  confirmButtonColor: "#10b981",
                                  cancelButtonColor: "#d33",
                                  confirmButtonText: "Sí, pagar",
                                  cancelButtonText: "Cancelar",
                                });
                                if (result.isConfirmed) executeShipmentCreation(methodId);
                              }}
                              onError={(msg) => { console.error("Stripe error:", msg); }}
                            />
                          </Elements>
                        ) : (
                          <div>
                            <p>No se pudo cargar la pasarela de pago.</p>
                            <button type="button" className={styles.btnBack}
                              onClick={() => fetchPaymentIntent(dynamicTotal, currentSelectedRate?.currency || "USD")}>
                              Reintentar
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: total + actions */}
                    <div className={styles.paymentRightCol}>
                      <div className={styles.paymentTotalCard}>
                        <span className={styles.paymentTotalLabel}>TOTAL A PAGAR</span>
                        <div className={styles.paymentTotalAmount}>
                          {dynamicTotal.toFixed(2)} {currentSelectedRate.currency}
                        </div>
                      </div>

                      <div className={styles.securityBadge}>
                        <FontAwesomeIcon icon={faShieldAlt} />
                        <span>Tus datos están seguros y encriptados</span>
                      </div>

                      <div className={styles.paymentActions}>
                        <button type="button" className={styles.btnBack} onClick={handleBackStep} disabled={isProcessingOrder}>
                          <FontAwesomeIcon icon={faChevronLeft} /> Atrás
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ---- Modals ---- */}
        <AddressCreationModal
          isOpen={showAddressModal}
          onClose={() => setShowAddressModal(false)}
          type={addressModalType}
          initialCountry={addressModalType === "destination" ? values.destination : "US"}
          addresses={addresses}
          onSuccess={async (modalData, savedAddress) => {
            const prefix = addressModalType;
            const refreshedAddresses = await customerAddressService.getAddresses();
            const allRefreshed = refreshedAddresses.all || [];
            setAddresses(allRefreshed);

            const sortedRefreshed = [...allRefreshed].sort((a, b) => (b.id || 0) - (a.id || 0));
            const actualSavedAddress = sortedRefreshed[0];

            if (actualSavedAddress?.id) {
              if (addressModalType === "origin") setSelectedOriginId(actualSavedAddress.id);
              else setSelectedDestId(actualSavedAddress.id);
            }

            setValues((prev: any) => ({
              ...prev,
              [`${prefix}Zip`]: modalData.zip,
              [`${prefix}Nickname`]: modalData.nickname,
              [`${prefix}AddressLine1`]: modalData.addressLine1,
              [`${prefix}AddressLine2`]: modalData.addressLine2 || "",
              [`${prefix}City`]: modalData.city,
              [`${prefix}State`]: modalData.state,
              [`${prefix}FirstName`]: modalData.firstName,
              [`${prefix}LastName`]: modalData.lastName,
              [`${prefix}Phone`]: actualSavedAddress?.addressSubjet?.phoneNumber || modalData.phoneNumber,
              [`${prefix}Email`]: actualSavedAddress?.addressSubjet?.email || modalData.email,
              [`${prefix}KindOfDNI`]: actualSavedAddress?.addressSubjet?.kindOfDNI || modalData.kindOfDNI,
              [`${prefix}DNI`]: actualSavedAddress?.addressSubjet?.dni || modalData.dni,
              ...(addressModalType === "destination" ? {
                receiverFirstName: actualSavedAddress?.addressSubjet?.firstName || modalData.firstName,
                receiverLastName: actualSavedAddress?.addressSubjet?.lastName || modalData.lastName,
                receiverEmail: actualSavedAddress?.addressSubjet?.email || modalData.email,
                receiverPhone: actualSavedAddress?.addressSubjet?.phoneNumber || modalData.phoneNumber,
                receiverKindOfDNI: actualSavedAddress?.addressSubjet?.kindOfDNI || modalData.kindOfDNI,
                receiverDNI: actualSavedAddress?.addressSubjet?.dni || modalData.dni,
                municipalityDestiny: modalData.city,
                stateDestiny: modalData.state,
              } : {}),
            }));
            if (addressModalType === "destination") {
              fetchMunicipality(modalData.city, modalData.state, modalData.zip, values.destination).then((loc) => {
                if (loc) setValues((v: any) => ({ ...v, municipalityDestiny: loc.municipality, stateDestiny: loc.state }));
              });
            }
            validateZip(addressModalType, modalData.zip, addressModalType === "origin" ? "US" : values.destination);
          }}
        />

        <EditPackageDataModal
          isOpen={showEditPackageModal}
          onClose={() => setShowEditPackageModal(false)}
          onSave={handleSavePackageData}
          initialData={{
            width: values.width || Number(packages[0]?.width) || "",
            height: values.height || Number(packages[0]?.height) || "",
            length: values.length || Number(packages[0]?.length) || "",
            weight: values.weight || Number(packages[0]?.weight) || "",
            fob: values.fob || Number(packages[0]?.fob) || "",
            unitSystem: values.unitSystem,
            parcelOrdDoc: values.parcelOrdDoc,
            currency: values.currency,
            packageContent: values.packageContent || packages[0]?.packageContent || "",
            pickupDate: values.pickupDate,
            hasBattery: values.hasBattery,
            selectedDocument: selectedDocument,
            packages: packages,
          }}
          currencies={currencies}
          isDropOff={!pickupSelected && !elpaketDispatcherSelected}
        />

        <PackageDetailsModal
          show={showPackageDetailsModal}
          onHide={() => setShowPackageDetailsModal(false)}
          packages={packages}
          unitSystem={values.unitSystem}
          currency={values.currency}
          parcelOrdDoc={values.parcelOrdDoc}
        />

        <EditAddressDataModal
          isOpen={showEditOriginModal}
          onClose={() => setShowEditOriginModal(false)}
          onSave={handleSaveOriginAddress}
          addresses={addresses}
          initialAddressId={selectedOriginId}
          title="Seleccionar Remitente"
          icon={faMapMarkerAlt}
          initialCountry="US"
          isDestination={false}
        />

        <EditAddressDataModal
          isOpen={showEditDestModal}
          onClose={() => setShowEditDestModal(false)}
          onSave={handleSaveDestAddress}
          addresses={addresses}
          initialAddressId={selectedDestId}
          title="Seleccionar Receptor"
          icon={faMapMarkerAlt}
          initialCountry={values.destination}
          isDestination={true}
        />

        <ServiceDetailModalV2
          show={showServiceDetailModal}
          onHide={() => { setShowServiceDetailModal(false); setSelectedRateForModal(null); }}
          rateOption={selectedRateForModal}
          selectedExternalIndices={selectedRateForModal ? selectedExternalIndicesByCarrier.get(getServiceKey(selectedRateForModal)) : new Set()}
          onSelectExternalService={(newIndices) => { if (selectedRateForModal) selectExternalService(selectedRateForModal, newIndices); }}
        />

        <EditAddressModal
          isOpen={showEditAddressModal}
          onClose={() => { setShowEditAddressModal(false); setAddressToEdit(null); }}
          address={addressToEdit}
          onSuccess={handleAddressUpdated}
        />
      </div>
    </DashboardLayout>
  );
};

export default SendPackagePageV3;
